import { getAuth } from 'firebase/auth';
import { z } from 'zod';
import {
  invoiceChangePlanSchema, invoiceChangeApplyResultSchema,
  invoiceDocumentHistoryDataSchema,
  invoiceLineageHistoryPageSchema,
  type InvoiceChangeIntent, type InvoiceChangeApplyRequest,
} from './invoiceSelectiveHistoryContracts';
import type { OnboardingStatusResponse, OnboardingSetupRequest, ChecklistItem, CreateFeedbackRequest } from './schemas';

// Permission Error class for handling 403 responses
export class PermissionError extends Error {
  permission: string;
  code: string;
  /**
   * 2026-08-04: `status` and `data` added so a PermissionError looks like every
   * OTHER error fetchAPI throws (those get `.status` + `.data` attached).
   *
   * Without them a 403 was invisible to downstream handlers that branch on the
   * normal error shape. OrgChangeStepper.tsx:696 has a good, actionable message
   * for `invoice_locked` ("Return it to Draft to change the client") gated on
   * `err.status === 403 || err.data?.code === 'invoice_locked'` - and BOTH were
   * undefined on a PermissionError, so that branch could never fire and the
   * user fell through to the generic handler. Combined with the 403 branch
   * reading the wrong envelope key, Christa got the literal string
   * "Permission denied" for what was actually a payment lock (invoice 0619,
   * 2026-08-04). Additive only: nothing previously read these.
   */
  status: number;
  data: any;

  constructor(
    message: string,
    permission: string,
    code: string = 'PERMISSION_DENIED',
    data: any = undefined,
  ) {
    super(message);
    this.name = 'PermissionError';
    this.permission = permission;
    this.code = code;
    this.status = 403;
    this.data = data;
  }
}

/**
 * Throw a PermissionError for a 403, reading the envelope the backend ACTUALLY
 * sends. Shared by `fetchAPI` and `handleResponse`, which had byte-identical
 * copies of this block — the shape of bug where a fix lands in one copy and the
 * other keeps shipping the old behavior.
 *
 * This app's deployed envelope is `{error: {...}, status_code}`, NOT
 * `{detail: {...}}`. For a status-based 403 like `invoice_locked`
 * (routers/invoices.py:1394, body `{error: {code, message, current_status}}`)
 * `errorData.detail` was undefined, so BOTH literal defaults below fired: the
 * server's actual sentence ("Cannot change client on an invoice in status
 * 'partially_paid'") was replaced with "Permission denied" and its real code
 * with "PERMISSION_DENIED", before any toast could read them. That is why
 * Christa saw a payment lock reported as a permissions problem (invoice 0619,
 * 2026-08-04). Envelope shapes pinned by
 * backend/tests/integration/test_locked_invoice_error_envelope.py.
 */
function throwIf403(status: number, errorData: any): void {
  if (status !== 403) return;
  const detail = errorData?.detail || errorData?.error || {};
  const permission = detail.required_permission || 'unknown';
  const message = detail.message || detail.error || 'Permission denied';
  throw new PermissionError(
    message,
    permission,
    detail.code || 'PERMISSION_DENIED',
    errorData,
  );
}
import type {
  Project,
  ProjectCreate,
  ProjectUpdate,
  ProjectListResponse,
  ProjectResponse,
  ProjectScope,
  ProjectScopeListResponse,
  ScopeType,
  ScopeTypeCreate,
  ScopeTypeUpdate,
  ScopeTypeListResponse,
  OrganizationCategory,
  OrganizationCategoryCreate,
  OrganizationCategoryUpdate,
  OrganizationCategoryListResponse,
  Organization,
  OrganizationCreate,
  OrganizationUpdate,
  OrganizationListResponse,
  OrganizationContact,
  OrganizationContactCreate,
  OrganizationContactUpdate,
  OrganizationContactListResponse,
  Establishment,
  EstablishmentCreate,
  EstablishmentUpdate,
  EstablishmentListResponse,
  Client,
  ClientCreate,
  ClientUpdate,
  ClientListResponse,
  BillableItem,
  BillableItemCreate,
  BillableItemUpdate,
  BillableItemListResponse,
  Invoice,
  InvoiceCreate,
  InvoiceCreateRequest,
  InvoiceUpdate,
  InvoiceListResponse,
  InvoiceItem,
  InvoiceItemCreate,
  InvoiceItemListResponse,
  DashboardPreferences,
  OrganizationSettings,
  InvoiceDefaults,
  BrandingSettings,
  ProposalDefaults,
  EmailSettings,
  FinancialSettings,
  SettingsResponse,
  DefaultCurrencySettings,
  ProposalConfig,
  ProjectCategoryOption,
  ProjectCategoryOptionCreate,
  ProjectCategoryOptionUpdate,
  ProjectFeeAssumption,
  ProjectFeeAssumptionCreate,
  ProjectFeeAssumptionUpdate,
  ProjectFeeAssumptionListResponse,
  ProjectFeeAssumptionResponse,
  ProjectFeeAssumptionReorderItem,
  OperationalSettings,
  AccountingContactCreate,
  AccountingContactUpdate,
  ScopeNote,
} from './schemas';
import type { ProjectListColorsConfig } from './projectListColors';
import type {
  Permit,
  PermitCreate,
  PermitUpdate,
  ListPermitsResponse,
  PermitResponse,
  DeletePermitResponse,
  AllPermitsResponse,
  AllPermitsQueryOptions,
  PermitTypesResponse,
  PermitTypeDefinition,
} from '../types/permits';
import {
  createFeedbackResponseSchema,
  feedbackListResponseSchema,
  roadmapResponseSchema,
  epicRoadmapResponseSchema,
  portalAcceptResponseSchema,
  portalUnAcceptResponseSchema,
  portalWithdrawResponseSchema,
  portalUnWithdrawResponseSchema,
  portalAddNoteResponseSchema,
  portalAnswerQuestionResponseSchema,
  portalUnseenCountResponseSchema,
  portalMarkSeenResponseSchema,
  portalPanelConfigResponseSchema,
  coordinationBoardResponseSchema,
  coordinationWorkItemFeedbackResponseSchema,
  coordinationPromoteResponseSchema,
  coordinationLinkResponseSchema,
  coordinationResolutionResponseSchema,
  coordinationPlanningResponseSchema,
  coordinationRepairLinkResponseSchema,
  coordinationMarkShippedResponseSchema,
  coordinationAskQuestionResponseSchema,
  coordinationDismissResponseSchema,
  epicDrillDownResponseSchema,
  projectSchema,
  projectListResponseSchema,
  projectResponseSchema,
  projectScopeSchema,
  projectScopeListResponseSchema,
  projectScopeSummaryResponseSchema, // R5 (v0.14): BillingTracksPanel
  scopeTypeSchema,
  scopeTypeListResponseSchema,
  organizationCategorySchema,
  organizationCategoryListResponseSchema,
  organizationSchema,
  organizationListResponseSchema,
  organizationContactSchema,
  organizationContactListResponseSchema,
  eligibleContactSchema,
  establishmentSchema,
  establishmentListResponseSchema,
  clientSchema,
  clientListResponseSchema,
  billableItemSchema,
  billableItemListResponseSchema,
  invoiceSchema,
  invoiceListResponseSchema,
  invoiceItemSchema,
  invoiceItemListResponseSchema,
  billingQueueResponseSchema,
  dashboardPreferencesSchema,
  organizationSettingsSchema,
  invoiceDefaultsSchema,
  brandingSettingsSchema,
  proposalDefaultsSchema,
  emailSettingsSchema,
  financialSettingsSchema,
  settingsResponseSchema,
  defaultCurrencySettingsSchema,
  proposalConfigSchema,
  projectCategoryOptionSchema,
  projectCategoryOptionListResponseSchema,
  scopeContactSchema,
  projectFeeAssumptionListResponseSchema,
  projectFeeAssumptionResponseSchema,
  userViewSchema,
  userViewListResponseSchema,
  operationalSettingsSchema,
  accountingContactSchema,
  accountingContactListResponseSchema,
  accountingContactResponseSchema,
  accountingContactRoleOrderResponseSchema,
  projectRecipientSetsResponseSchema,
  invoiceRecipientPreviewSchema,
  scopeNoteSchema,
  taxSettingsSchema,
  taxEngineStatusSchema,
  siteVisitDefaultsSchema,
  orgChangeSearchResponseSchema,
} from './schemas';
import type { TaxSettings, TaxEngineStatus, SiteVisitDefaults } from './schemas';
import {
  mergeApplyResponseSchema,
  mergePreviewResponseSchema,
  type MergeApplyRequest,
  type MergeApplyResult,
  type MergePreviewPdfRequest,
  type MergePreviewRequest,
} from './invoice-merge-plan';
import type {
  HeldPayment,
  HoldOutcome,
  ReconcileResult,
} from './invoice-payment-reconciliation';

// NOTE: Permit schemas will use z.any() until proper Zod schemas are created

const API_BASE_URL = import.meta.env.VITE_API_URL;
if (!API_BASE_URL) {
  throw new Error('VITE_API_URL environment variable is required. Check your .env file.');
}

async function getAuthToken(forceRefresh = false): Promise<string> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No authenticated user');
  }
  // forceRefresh=true bypasses the cached Firebase ID token and mints a fresh
  // one — used by the 401 retry path below when a long-lived tab's cached token
  // has expired server-side but Firebase hasn't rotated it client-side yet.
  return user.getIdToken(forceRefresh);
}

/**
 * Hard deadline for a READ, covering token acquisition AND both fetch attempts.
 *
 * 2026-09-25: Christa reported that she "couldn't add a new invoice". The Create
 * Invoice button is gated on `useNextInvoiceNumber` resolving
 * (InvoiceCreatePage.tsx), and NOTHING in this stack had a timeout:
 *   - the service worker's `networkTimeoutSeconds: 10` only matches same-origin
 *     `/api/` (workers/sw.ts) and the API is a different origin, so every real
 *     call bypasses the SW entirely;
 *   - `fetch` carried no AbortSignal;
 *   - React Query's `retry: 1` (main.tsx) never fires, because a stalled socket
 *     never *fails* — it just never settles.
 * One dead connection therefore left the button disabled indefinitely, with no
 * error and no toast, until a full page reload. Reproduced against production:
 * disabled at 6s, 26s and 56s with zero further requests.
 *
 * 20s is ~4x the slowest GET seen in a week of production Cloud Run logs
 * (p50 0.32s, p95 2.67s, p99 4.10s, max 4.55s — the ~4.5s cluster is one
 * cold-start burst), so it cannot fire on a merely slow read, only on one that
 * has genuinely stopped. Once it fires the read becomes a normal rejection and
 * `retry: 1` re-issues it on a fresh connection.
 */
const READ_REQUEST_TIMEOUT_MS = 20_000;

/**
 * An abort deadline that also composes with a caller-supplied signal.
 *
 * Returns `expiry`, a promise that rejects when the deadline (or the caller)
 * aborts, so non-fetch awaits inside the request — getIdToken() — can be raced
 * against the same clock. `dispose()` MUST run in a finally, or a long-lived
 * page leaks one timer and one listener per request.
 */
function createDeadline(callerSignal: AbortSignal | null | undefined, ms: number) {
  const controller = new AbortController();
  const timer = setTimeout(
    () => controller.abort(new DOMException(`Request exceeded ${ms}ms`, 'TimeoutError')),
    ms
  );

  const onCallerAbort = () => controller.abort(callerSignal?.reason);
  if (callerSignal) {
    if (callerSignal.aborted) controller.abort(callerSignal.reason);
    else callerSignal.addEventListener('abort', onCallerAbort, { once: true });
  }

  const expiry = new Promise<never>((_, reject) => {
    if (controller.signal.aborted) reject(controller.signal.reason);
    else
      controller.signal.addEventListener(
        'abort',
        () => reject(controller.signal.reason),
        { once: true }
      );
  });
  // The request usually wins the race, leaving `expiry` rejected-but-unawaited.
  // Attach a sink so that never surfaces as an unhandled rejection.
  expiry.catch(() => {});

  return {
    signal: controller.signal,
    expiry,
    dispose: () => {
      clearTimeout(timer);
      callerSignal?.removeEventListener('abort', onCallerAbort);
    },
  };
}

/**
 * Theme 5 (failure resilience): single fetch wrapper that injects the Firebase
 * ID token and transparently recovers from ONE expired-token 401.
 *
 * Long-lived tabs (Christa leaves the invoice page open all afternoon) can hit
 * the API with a cached ID token that the backend has already rejected as
 * expired. Before this, the user saw an opaque 401 and had to hard-reload to
 * re-auth — there is no app-level reauth flow. Now: on a 401 we force-refresh
 * the token (`getIdToken(true)`) and retry the SAME request exactly once. A
 * second 401 is surfaced normally (genuinely signed out / revoked).
 *
 * One retry only — deliberately NOT a loop. This is auth-token refresh, not a
 * transient-failure retry policy (mutations stay `retry: 0`, see main.tsx).
 *
 * The caller owns response handling (Zod parse / handleResponse); this helper
 * only handles the auth header + the 401 reauth retry.
 *
 * READS (GET) carry a hard deadline; writes do NOT. Aborting a mutation
 * mid-flight would report failure for work the server may have already applied
 * — a double-send or a duplicate invoice — which is the same hazard that keeps
 * mutations at `retry: 0` in main.tsx. File uploads, downloads, PDF fetches and
 * CSV exports use raw `fetch` elsewhere and never reach this wrapper, so the
 * legitimately long-running calls are untouched.
 */
async function fetchWithAuth(url: string, options?: RequestInit): Promise<Response> {
  const buildHeaders = (token: string): HeadersInit => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    ...options?.headers,
  });

  // Reads get a deadline; writes deliberately do not. See READ_REQUEST_TIMEOUT_MS.
  const isRead = (options?.method ?? 'GET').toUpperCase() === 'GET';
  const deadline = isRead ? createDeadline(options?.signal, READ_REQUEST_TIMEOUT_MS) : null;
  // The deadline covers token acquisition too: getIdToken() can itself hit the
  // network to mint a fresh token, and a stall there means the request is never
  // even sent — which is invisible in server logs.
  // `signal` asks fetch to abort (freeing the socket); racing `expiry` as well
  // makes the deadline unconditional, so the promise settles even if the
  // underlying fetch ignores the signal.
  const guard = <T,>(p: Promise<T>): Promise<T> =>
    deadline ? Promise.race([p, deadline.expiry]) : p;
  const signal = deadline ? deadline.signal : options?.signal;

  try {
    const token = await guard(getAuthToken());
    let response = await guard(fetch(url, { ...options, headers: buildHeaders(token), signal }));

    // Expired-token recovery: force a fresh token and retry once.
    if (response.status === 401) {
      try {
        const freshToken = await guard(getAuthToken(true));
        response = await guard(fetch(url, { ...options, headers: buildHeaders(freshToken), signal }));
      } catch {
        // Refresh failed (e.g. user truly signed out), or the read deadline
        // expired mid-refresh — fall through with the original 401 response so
        // the caller surfaces a normal auth error rather than hanging.
      }
    }

    return response;
  } finally {
    deadline?.dispose();
  }
}

/**
 * Extract a human-readable error message from a 4xx/5xx JSON body.
 *
 * The deployed backend produces several error shapes; we try them in order
 * of specificity and ALWAYS return a string (never an object → guards
 * against `new Error({})` rendering as the literal `"[object Object]"`):
 *
 *   1. FastAPI array detail (Pydantic):
 *        {detail: [{msg: "x"}, {msg: "y"}]} → "x; y"
 *   2. FastAPI string detail:
 *        {detail: "msg"} → "msg"
 *   3. FastAPI structured detail:
 *        {detail: {error: "code", message: "human"}}
 *        → prefer detail.message, fall back to detail.error if string.
 *   4. This app's wrapped envelope (main.py wraps HTTPException.detail
 *      under the JSON key `error`):
 *        {error: {error: "cancel_blocked_invoice_sent", message: "...",
 *                 blocking_invoices: [...]}, status_code: 409, ...}
 *        → prefer error.message, fall back to error.error if string.
 *   5. Flat shapes:
 *        {error: "msg"} or {message: "msg"} → use as-is (when string).
 *   6. Fallback: response.statusText (NEVER `"[object Object]"`).
 *
 * Note: when we hit shape 4 the structured object is preserved on `error.data`,
 * so callers reading `error.data.error.blocking_invoices` keep working — this
 * function only computes the human-readable `error.message` / toast string.
 */
export class DocumentBytesUnavailableError extends Error {
  readonly code = 'document_bytes_unavailable';
  constructor() {
    super('No stored copy of this saved PDF is available.');
    this.name = 'DocumentBytesUnavailableError';
  }
}

function extractErrorMessage(errorData: any, statusText: string): string {
  const fallback = statusText || 'Request failed';

  if (!errorData || typeof errorData !== 'object') {
    return fallback;
  }

  // (1) FastAPI Pydantic validation: detail is an array of {msg, ...}
  const rawDetail = errorData.detail;
  if (Array.isArray(rawDetail)) {
    const joined = rawDetail
      .map((d: any) => d?.msg || d?.message || (typeof d === 'string' ? d : JSON.stringify(d)))
      .filter(Boolean)
      .join('; ');
    if (joined) return joined;
  }

  // (2) FastAPI raw string detail
  if (typeof rawDetail === 'string' && rawDetail) {
    return rawDetail;
  }

  // (3) FastAPI raw structured detail: {detail: {error, message, ...}}
  if (rawDetail && typeof rawDetail === 'object' && !Array.isArray(rawDetail)) {
    if (typeof rawDetail.message === 'string' && rawDetail.message) return rawDetail.message;
    if (typeof rawDetail.error === 'string' && rawDetail.error) return rawDetail.error;
  }

  // (4) This app's wrapped envelope: {error: {error, message, ...}, status_code, ...}
  const rawError = errorData.error;
  if (rawError && typeof rawError === 'object' && !Array.isArray(rawError)) {
    if (typeof rawError.message === 'string' && rawError.message) return rawError.message;
    if (typeof rawError.error === 'string' && rawError.error) return rawError.error;
    // 2026-08-03: `detail` was missing here, so the 409 line-item guard family
    // lost its reason. `invoice_not_editable_for_items` / `_for_billto` raise
    // HTTPException(detail={"detail": ..., "code": ...}), which the envelope
    // wraps as {error: {detail, code}} with NO `message` and no `error` string.
    // Branch (5) can't help (rawError is an object, not a string), so it fell
    // through to `statusText`, which is ALWAYS "" over HTTP/2, and the user saw
    // a bare "Request failed".
    //
    // 2026-08-04 correction, measured on the wire by
    // tests/integration/test_locked_invoice_error_envelope.py: the 403
    // `invoice_locked` is NOT in this class. It raises
    // detail={"code", "message", "current_status"}, so it HAS a `message` and
    // the branch above already returned it. The 403's own problem was upstream
    // of here (the handler read the wrong envelope key and clobbered both the
    // message and the code) and is fixed in the 403 branch below.
    //
    // Still open, deliberately NOT fixed here: useInvoices.ts:338 catches any
    // `err?.status === 403` and hardcodes "You don't have permission to add
    // recipients to this invoice.", discarding err.message. That is scoped to
    // the recipients hook, not the client-change path, so it is a separate
    // change in useInvoices.ts. Note this function can be green while that
    // handler still mislabels a status lock - it computes the message, it does
    // not control who throws it away.
    //
    // Both envelopes are pinned by that integration test so this comment can't
    // drift from reality again. (An earlier draft of it named
    // `cascadeErrorToast`, which exists only on the staging lineage - the
    // hazard this note now guards against.)
    if (typeof rawError.detail === 'string' && rawError.detail) return rawError.detail;
  }

  // (5) Flat shapes
  if (typeof rawError === 'string' && rawError) return rawError;
  if (typeof errorData.message === 'string' && errorData.message) return errorData.message;

  // (6) Fallback — never return `"[object Object]"`.
  return fallback;
}

/**
 * Super-admin destructive-op confirm header (backend:
 * utils/super_admin_destructive_guard.py). The backend gates super-admins on
 * production tenants for status DOWNGRADES (paid→sent, sent→ready, ready→draft,
 * any→cancelled) and payment VOIDS, requiring this header to carry the EXACT
 * entity_id being modified (the invoice id) — else 403. It also rate-limits to
 * 10 destructive ops / 60s → 429 with a cooldown. The web UI never sent this
 * header, so a super-admin was the only role that hit 403 on these routine ops.
 *
 * `superAdminConfirmHeader(invoiceId)` builds the one-request header; callers
 * attach it ONLY on the specific confirmed request (never globally) after the
 * user clears the confirm modal.
 */
export const SUPER_ADMIN_CONFIRM_HEADER = 'X-Super-Admin-Confirm-Destructive';

export function superAdminConfirmHeader(entityId: string): Record<string, string> {
  return { [SUPER_ADMIN_CONFIRM_HEADER]: entityId };
}

/**
 * True when an error came from the backend's super-admin destructive-op rate
 * limiter (HTTP 429). Callers map this to a clear cooldown message instead of a
 * generic failure toast. The backend returns 429 both on the first trip
 * (10 ops/60s) and during the 5-min cooldown.
 */
export function isSuperAdminRateLimitError(error: unknown): boolean {
  return !!error && typeof error === 'object' && (error as { status?: number }).status === 429;
}

/**
 * Read a `Retry-After` response header (RFC 9110 §10.2.3 — either an integer
 * delta-seconds or an HTTP-date) and return it in milliseconds, if present
 * and sane. Undefined otherwise, so the caller's own backoff schedule
 * (queryClientDefaults.ts) applies unchanged.
 *
 * This is the preview/staging single-instance capacity work (2026-09-14):
 * the backend's own super-admin destructive-op limiter and, more relevantly,
 * Cloud Run's 429 admission-control response while the single instance is
 * cold-starting, both may carry this header. When they do, waiting exactly
 * that long (rather than guessing via exponential backoff) both respects the
 * server's own signal and avoids retrying too early into a still-cold
 * instance.
 */
function parseRetryAfterMs(response: Response): number | undefined {
  // Defensive: plenty of existing tests mock a bare {ok, status, json}
  // response with no `headers` at all. Never let reading a header crash the
  // error path that a hundred unrelated tests already exercise.
  const header = response.headers?.get?.('Retry-After');
  if (!header) return undefined;
  const seconds = Number(header);
  if (Number.isFinite(seconds) && seconds >= 0) {
    return seconds * 1000;
  }
  // Fall back to the HTTP-date form.
  const dateMs = Date.parse(header);
  if (!Number.isNaN(dateMs)) {
    const deltaMs = dateMs - Date.now();
    return deltaMs > 0 ? deltaMs : 0;
  }
  return undefined;
}

// Generic fetch wrapper with Zod validation
async function fetchAPI<T>(
  endpoint: string,
  schema: z.ZodType<T>,
  options?: RequestInit
): Promise<T> {
  // Theme 5: routed through fetchWithAuth so an expired-token 401 is recovered
  // (force-refresh + retry once) before we treat it as a real failure.
  const response = await fetchWithAuth(`${API_BASE_URL}${endpoint}`, options);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    throwIf403(response.status, errorData);

    // Handle every 4xx/5xx shape the deployed backend produces — see
    // extractErrorMessage's docstring. Critically, this app's main.py wraps
    // HTTPException.detail under the JSON key `error`, so `errorData.error`
    // is often a structured OBJECT; passing it raw to `new Error(obj)` used
    // to produce the literal `"[object Object]"` toast.
    const errorMessage = extractErrorMessage(errorData, response.statusText);
    const error: any = new Error(errorMessage);
    error.status = response.status;
    error.data = errorData;
    // Read once per failed request; undefined when absent (the common case).
    // Only queryClientDefaults.ts's retry policy reads this.
    error.retryAfterMs = parseRetryAfterMs(response);
    throw error;
  }

  const data = await response.json();

  const result = schema.safeParse(data);
  if (!result.success) {
    if (import.meta.env.DEV) {
      console.warn(`[Zod] ${endpoint}:`, result.error.issues);
    }
    return data;
  }
  // v0.14.3 follow-up: on a SUCCESSFUL parse Zod strips any key not declared
  // in the schema — which silently killed 5 shipped features (see
  // docs/releases/v0.14.3.md). Surface stripped keys in vitest output so the
  // gap is visible the moment a test mocks a field the schema lacks.
  // Test mode only — compiled out of dev/staging/prod bundles.
  if (import.meta.env.MODE === 'test') {
    warnOnStrippedKeys(endpoint, data, result.data);
  }
  return result.data;
}

/**
 * Keys the schemas INTENTIONALLY strip and which we never want a component to
 * read. Suppressed from the [Zod-strip] warning so the remaining warnings are
 * pure signal (a genuine schema gap the moment a mock surfaces a real field).
 *
 * SPEC-test-suite-honesty R5(a): only `tenant_id` qualifies today. The backend
 * DOES return tenant_id on most rows (organizations.py / projects SELECT), but
 * the frontend is tenant-scoped via the JWT and must NEVER branch on a
 * response-body tenant_id — so every entity schema drops it by design
 * ("tenant_id is NOT returned by backend API" comments in schemas.ts are
 * aspirational; the strip is the real enforcement). Adding a NEW entry here is
 * a deliberate "this strip is correct" decision — do not add a key just to
 * quiet a warning; that's how the v0.14.3 dead features shipped.
 */
const INTENTIONALLY_STRIPPED_KEYS = new Set<string>(['tenant_id']);

/**
 * Recursively diff raw response keys against the Zod-parsed result and warn
 * about anything the parse dropped. Depth-limited; arrays compare their
 * first element (list endpoints share one shape). Warn-only — existing mocks
 * carry junk extra fields, so throwing would be pure noise.
 */
function warnOnStrippedKeys(endpoint: string, raw: unknown, parsed: unknown, path = '', depth = 0): void {
  if (depth > 3 || raw === null || parsed === null || typeof raw !== 'object' || typeof parsed !== 'object') return;
  if (Array.isArray(raw)) {
    if (Array.isArray(parsed) && raw.length > 0 && parsed.length > 0) {
      warnOnStrippedKeys(endpoint, raw[0], parsed[0], `${path}[0]`, depth + 1);
    }
    return;
  }
  const rawObj = raw as Record<string, unknown>;
  const parsedObj = parsed as Record<string, unknown>;
  for (const key of Object.keys(rawObj)) {
    if (!(key in parsedObj)) {
      // Suppress documented-intentional strips so the signal stays clean.
      if (INTENTIONALLY_STRIPPED_KEYS.has(key)) continue;
      console.warn(
        `[Zod-strip] ${endpoint}: response field "${path}${path ? '.' : ''}${key}" was STRIPPED by the schema — ` +
        `if a component reads it, declare it in src/lib/schemas.ts (see docs/releases/v0.14.3.md)`
      );
    } else {
      warnOnStrippedKeys(endpoint, rawObj[key], parsedObj[key], `${path}${path ? '.' : ''}${key}`, depth + 1);
    }
  }
}

// Helper function for low-level API methods (handles errors without Zod validation)
async function handleResponse(response: Response): Promise<unknown> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));

    // Handle 403 Permission Denied specially
    throwIf403(response.status, errorData);

    // Mirror fetchAPI: cover every error shape via the shared helper so
    // structured errors (notably this app's wrapped {error: {...}} envelope)
    // produce a real message instead of "[object Object]".
    const errorMessage = extractErrorMessage(errorData, response.statusText);
    const error: any = new Error(errorMessage);
    error.status = response.status;
    error.data = errorData;
    error.retryAfterMs = parseRetryAfterMs(response);
    throw error;
  }

  return response.json();
}

// ============================================================================
// Structured API Client with Type Safety
// ============================================================================

// Types for external stamp upload / inspector result
export interface ExternalStampSigner {
  subject_dn: string;
  issuer_dn: string;
  claimed_signing_time: string;
  not_before: string;
  not_after: string;
}

export interface ExternalStampInspectorResult {
  is_signed: boolean;
  signers: ExternalStampSigner[];
  known_ca_match: string | null;
  issuer_dn_short?: string | null;
  pdf_sha256: string;
  differs_from_reference: boolean;
}

export interface UploadHTTPError extends Error {
  httpStatus: number;
}

function makeUploadHTTPError(message: string, httpStatus: number): UploadHTTPError {
  const e = new Error(message) as UploadHTTPError;
  e.httpStatus = httpStatus;
  return e;
}

export const api = {
  // Low-level HTTP methods for direct endpoint access.
  // Theme 5: routed through fetchWithAuth so an expired-token 401 is recovered
  // (force-refresh + retry once) on the primary api.* mutation surface.
  get: async (endpoint: string) => {
    const response = await fetchWithAuth(`${API_BASE_URL}${endpoint}`);
    return handleResponse(response);
  },

  post: async (endpoint: string, data?: unknown) => {
    const response = await fetchWithAuth(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
    return handleResponse(response);
  },

  put: async (endpoint: string, data?: unknown) => {
    const response = await fetchWithAuth(`${API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
    return handleResponse(response);
  },

  delete: async (endpoint: string) => {
    const response = await fetchWithAuth(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },

  // Projects
  projects: {
    list: (params?: {
      limit?: number;
      offset?: number;
      status?: string;
      search?: string;
      workflow_stage?: string;
      category?: string;
      has_action_needed?: boolean;
      updated_within_days?: number;
      exclude_active_proposals?: boolean;  // Phase 9.6: Exclude proposals in Active tab from Archived
      is_template?: boolean;
      has_project_number?: boolean;  // Phase 7.5.19: Filter by project_number presence
      is_archived?: boolean;  // V108: Soft-delete archive filter
      include_archived?: boolean;  // #150: include BOTH archived + active (Proposals → Archived)
      // R2 AC5: only projects that have a client billing track of this client (the "project is moving" picker).
      client_organization_id?: string;
      sort_by?: string;
      sort_order?: string;
    }) =>
      fetchAPI(
        `/api/projects?${new URLSearchParams(
          Object.entries(params || {})
            .filter(([_, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)])
        )}`,
        projectListResponseSchema
      ),

    get: (id: string) =>
      fetchAPI(`/api/projects/${id}`, projectResponseSchema),

    getNextNumber: () =>
      fetchAPI(`/api/projects/next-number`, z.object({
        success: z.boolean(),
        next_number: z.number()
      })),

    // Phase 9.6c: Get next project number preview (category-based, doesn't increment)
    getNextNumberPreview: (categoryId: string) =>
      fetchAPI(`/api/projects/next-number?category_id=${categoryId}`, z.object({
        success: z.boolean(),
        next_number: z.string(),
      })),

    create: (data: ProjectCreate) =>
      fetchAPI(`/api/projects`, projectResponseSchema, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: ProjectUpdate) =>
      fetchAPI(`/api/projects/${id}`, projectResponseSchema, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchAPI(`/api/projects/${id}`, z.object({ success: z.boolean() }), {
        method: 'DELETE',
      }),

    restore: (id: string) =>
      fetchAPI(`/api/projects/${id}/restore`, z.object({ success: z.boolean() }), {
        method: 'POST',
      }),

    // Phase 7 Batch 7.3: Proposal workflows
    convertProposal: (projectId: string, data: {
      item_decisions: Array<{ item_id: string; action: 'confirm' | 'decline' }>;
      fee_assumptions?: string;
      update_accepted_timestamp?: boolean;
    }) =>
      fetchAPI(`/api/projects/${projectId}/convert-proposal-to-project`, z.object({ success: z.boolean(), project: projectSchema }), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    // Phase 7.5.10: Mark proposal as sent to client
    sendProposal: (projectId: string) =>
      fetchAPI(`/api/projects/${projectId}/send-proposal`, z.object({ success: z.boolean(), project: projectSchema }), {
        method: 'POST',
      }),

    duplicate: (projectId: string) =>
      fetchAPI(`/api/projects/${projectId}/duplicate`, z.object({
        success: z.boolean(),
        project: projectSchema,
        scopes_copied: z.number().optional(),
        items_copied: z.number().optional(),
      }), {
        method: 'POST',
      }),

    copyTemplate: (templateId: string) =>
      fetchAPI(`/api/projects/${templateId}/copy-template`, z.object({
        success: z.boolean(),
        template: projectSchema,
        scopes_copied: z.number().optional(),
        items_copied: z.number().optional(),
      }), {
        method: 'POST',
      }),

    createFromTemplate: (templateId: string, data: {
      organization_id: string;
      name?: string;
      address?: string;
      notes?: string;
      client_contact_id?: string;
      // 2026-09-13 template-category fix: omit to inherit the template's category;
      // include (even as null) to override it. See CreateFromTemplateRequest (backend).
      category?: string | null;
    }) =>
      fetchAPI(`/api/projects/${templateId}/create-from-template`, z.object({
        success: z.boolean(),
        project: projectSchema,
        scopes_copied: z.number().optional(),
        items_copied: z.number().optional(),
      }), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    convertToTemplate: (projectId: string) =>
      fetchAPI(`/api/projects/${projectId}/save-as-template`, z.object({
        success: z.boolean(),
        template: projectSchema,
        scopes_copied: z.number().optional(),
        items_copied: z.number().optional(),
      }), {
        method: 'POST',
      }),

    // Phase 8.3: Proposal PDF download
    downloadProposalPDF: async (projectId: string, forceRegenerate: boolean = false): Promise<Blob> => {
      const token = await getAuthToken();
      const url = `${API_BASE_URL}/api/projects/${projectId}/proposal-pdf/download${forceRegenerate ? '?force_regenerate=true' : ''}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const rawMessage = errorData.detail || errorData.error || errorData.message || response.statusText || 'Unknown error';
        const errorMessage = typeof rawMessage === 'string' ? rawMessage : JSON.stringify(rawMessage);
        const error: any = new Error(errorMessage);
        error.status = response.status;
        error.data = errorData;
        throw error;
      }

      return response.blob();
    },

    // Phase 10c.9: Per-project cover image
    getCoverImage: (projectId: string) =>
      fetchAPI<{ success: boolean; cover_image_base64: string | null }>(
        `/api/projects/${projectId}/cover-image`,
        z.object({
          success: z.boolean(),
          cover_image_base64: z.string().nullable(),
        })
      ),

    updateCoverImage: (projectId: string, cover_image_base64: string | null) =>
      fetchAPI<{ success: boolean; message: string }>(
        `/api/projects/${projectId}/cover-image`,
        z.object({ success: z.boolean(), message: z.string() }),
        {
          method: 'PUT',
          body: JSON.stringify({ cover_image_base64 }),
        }
      ),

    // R1: PATCH per-project add-in visibility overrides.
    // Backend stores whatever overrides map is sent — frontend handles deep-merge.
    updateSectionConfig: (projectId: string, overrides: Record<string, boolean>) =>
      fetchAPI(
        `/api/projects/${projectId}/section-config`,
        z.object({ success: z.boolean(), project: z.any() }),
        {
          method: 'PATCH',
          body: JSON.stringify({ overrides }),
        }
      ),

    // R1 (v0.14, W2 kill): Smart-default establishment hint for InvoiceCreatePage.
    // Backend always returns 200 — null payload when there's no invoice history.
    // Caller should treat a null suggested_establishment_id as "no hint" and fall
    // through to the picker's local decision tree (single-est / placeholder).
    // What a NEW invoice for these inputs would go to (server-side, the same code create uses).
    getInvoiceRecipientPreview: (
      projectId: string,
      params: { organization_id: string; establishment_id?: number | null; client_scope_id?: string | null },
    ) => {
      const q = new URLSearchParams({ organization_id: params.organization_id });
      if (params.establishment_id !== undefined && params.establishment_id !== null) {
        q.set('establishment_id', String(params.establishment_id));
      }
      if (params.client_scope_id) q.set('client_scope_id', params.client_scope_id);
      return fetchAPI(
        `/api/projects/${projectId}/invoice-recipient-preview?${q.toString()}`,
        invoiceRecipientPreviewSchema,
      );
    },

    getInvoiceEstablishmentHint: (
      projectId: string,
      params?: { organization_id?: string; client_scope_id?: string }
    ) =>
      fetchAPI(
        `/api/projects/${projectId}/invoice-establishment-hint?${new URLSearchParams(
          Object.entries(params || {})
            .filter(([_, v]) => v !== undefined && v !== '')
            .map(([k, v]) => [k, String(v)])
        )}`,
        z.object({
          suggested_establishment_id: z.number().int().nullable(),
          source: z.enum(['project_scope_history', 'organization_history']).nullable(),
          source_invoice_number: z.string().nullable(),
          source_invoice_date: z.string().nullable(),
        })
      ),
  },

  projectCategories: {
    list: (params?: { include_inactive?: boolean }) =>
      fetchAPI(
        `/api/project-category-options?${new URLSearchParams(
          Object.entries(params || {})
            .filter(([_, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)])
        )}`,
        projectCategoryOptionListResponseSchema
      ),

    create: (data: ProjectCategoryOptionCreate) =>
      fetchAPI(`/api/project-category-options`, z.object({ success: z.boolean(), category: projectCategoryOptionSchema }), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: ProjectCategoryOptionUpdate) =>
      fetchAPI(`/api/project-category-options/${id}`, z.object({ success: z.boolean(), category: projectCategoryOptionSchema }), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchAPI(`/api/project-category-options/${id}`, z.object({ success: z.boolean() }), {
        method: 'DELETE',
      }),
  },

  // Project Scopes (Phase 5 - Organization Relationships)
  projectScopes: {
    list: (projectId: string) =>
      fetchAPI(`/api/projects/${projectId}/scopes`, projectScopeListResponseSchema),

    create: (data: { projectId: string; organizationId: string; scopeTypeId: string; contactId?: string; status?: string; statusNotes?: string }) => {
      const { projectId, organizationId, scopeTypeId, contactId, status, statusNotes } = data;
      const requestBody: Record<string, any> = {
        organization_id: organizationId,
        scope_type_id: scopeTypeId,
      };
      if (contactId) requestBody.contact_id = contactId;
      if (status) requestBody.status = status;
      if (statusNotes) requestBody.status_notes = statusNotes;

      return fetchAPI(`/api/projects/${projectId}/scopes`, z.object({ success: z.boolean(), scope: projectScopeSchema }), {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });
    },

    update: (projectId: string, scopeId: string, data: {
      organization_id?: string;
      scope_type_id?: string;
      contact_id?: string | null;
      status?: string;
      status_notes?: string;
      // v0.12.22 Fix 2: project-default establishment override.
      establishment_id?: number | null;
    }) => {
      const requestBody: Record<string, any> = {};
      // Issue #13: Send all editable fields, not just status
      if (data.organization_id) requestBody.organization_id = data.organization_id;
      if (data.scope_type_id) requestBody.scope_type_id = data.scope_type_id;
      if (data.contact_id !== undefined) requestBody.contact_id = data.contact_id; // Allow null
      if (data.status) requestBody.status = data.status;
      if (data.status_notes !== undefined) requestBody.status_notes = data.status_notes; // Allow empty string
      // v0.12.22 Fix 2: forward establishment_id (allow null to clear)
      if (data.establishment_id !== undefined) requestBody.establishment_id = data.establishment_id;

      return fetchAPI(`/api/projects/${projectId}/scopes/${scopeId}`, z.object({ success: z.boolean(), scope: projectScopeSchema }), {
        method: 'PUT',
        body: JSON.stringify(requestBody),
      });
    },

    delete: (projectId: string, scopeId: string) =>
      fetchAPI(`/api/projects/${projectId}/scopes/${scopeId}`, z.object({ success: z.boolean() }), {
        method: 'DELETE',
      }),

    // Phase 9.7bc: Set scope as primary client
    setPrimary: (projectId: string, scopeId: string) =>
      fetchAPI(`/api/projects/${projectId}/scopes/${scopeId}/set-primary`, z.object({ success: z.boolean(), scope: projectScopeSchema }), {
        method: 'PUT',
      }),

    // R5 (v0.14): per-scope invoice summary for BillingTracksPanel
    summary: (projectId: string) =>
      fetchAPI(`/api/projects/${projectId}/scope-summary`, projectScopeSummaryResponseSchema),
  },

  // Phase 9.7bc: Scope Contacts (junction table - multiple contacts per scope)
  scopeContacts: {
    list: (projectId: string, scopeId: string) =>
      fetchAPI(`/api/projects/${projectId}/scopes/${scopeId}/contacts`, z.object({ success: z.boolean(), contacts: z.array(scopeContactSchema) })),

    add: (projectId: string, scopeId: string, data: { contact_id: string; is_primary_contact?: boolean; is_invoice_contact?: boolean }) =>
      fetchAPI(`/api/projects/${projectId}/scopes/${scopeId}/contacts`, z.object({ success: z.boolean(), scope_contact: scopeContactSchema }), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (projectId: string, scopeId: string, contactId: string, data: { is_primary_contact?: boolean; is_invoice_contact?: boolean }) =>
      fetchAPI(`/api/projects/${projectId}/scopes/${scopeId}/contacts/${contactId}`, z.object({ success: z.boolean(), scope_contact: scopeContactSchema }), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    remove: (projectId: string, scopeId: string, contactId: string) =>
      fetchAPI(`/api/projects/${projectId}/scopes/${scopeId}/contacts/${contactId}`, z.object({ success: z.boolean() }), {
        method: 'DELETE',
      }),

    setPrimary: (projectId: string, scopeId: string, contactId: string) =>
      fetchAPI(`/api/projects/${projectId}/scopes/${scopeId}/contacts/${contactId}/set-primary`, z.object({ success: z.boolean(), scope_contact: scopeContactSchema }), {
        method: 'PUT',
      }),

    setInvoice: (projectId: string, scopeId: string, contactId: string) =>
      fetchAPI(`/api/projects/${projectId}/scopes/${scopeId}/contacts/${contactId}/set-invoice`, z.object({ success: z.boolean(), scope_contact: scopeContactSchema }), {
        method: 'PUT',
      }),
  },

  // R5: Scope Notes — timestamped notes per scope
  scopeNotes: {
    list: (projectId: string, scopeId: string) =>
      fetchAPI(`/api/projects/${projectId}/scopes/${scopeId}/notes`, z.object({ success: z.boolean(), notes: z.array(scopeNoteSchema), total: z.number() })),

    create: (projectId: string, scopeId: string, noteText: string) =>
      fetchAPI(`/api/projects/${projectId}/scopes/${scopeId}/notes`, z.object({ success: z.boolean(), note: scopeNoteSchema }), {
        method: 'POST',
        body: JSON.stringify({ note_text: noteText }),
      }),
  },

  // Scope Types (Phase 5 Batch 3 - Tenant-configurable scope types)
  scopeTypes: {
    list: () =>
      fetchAPI(`/api/scope-types`, scopeTypeListResponseSchema),

    get: (id: string) =>
      fetchAPI(`/api/scope-types/${id}`, z.object({ success: z.boolean(), scope_type: scopeTypeSchema })),

    create: (data: ScopeTypeCreate) =>
      fetchAPI(`/api/scope-types`, z.object({ success: z.boolean(), scope_type: scopeTypeSchema }), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: ScopeTypeUpdate) =>
      fetchAPI(`/api/scope-types/${id}`, z.object({ success: z.boolean(), scope_type: scopeTypeSchema }), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchAPI(`/api/scope-types/${id}`, z.object({ success: z.boolean() }), {
        method: 'DELETE',
      }),
  },

  // Organization Categories (Phase 6 Batch 2 - Tenant-configurable)
  organizationCategories: {
    list: () =>
      fetchAPI(`/api/organization-categories`, organizationCategoryListResponseSchema),

    get: (id: string) =>
      fetchAPI(`/api/organization-categories/${id}`, z.object({ success: z.boolean(), category: organizationCategorySchema })),

    create: (data: OrganizationCategoryCreate) =>
      fetchAPI(`/api/organization-categories`, z.object({ success: z.boolean(), category: organizationCategorySchema }), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: OrganizationCategoryUpdate) =>
      fetchAPI(`/api/organization-categories/${id}`, z.object({ success: z.boolean(), category: organizationCategorySchema }), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchAPI(`/api/organization-categories/${id}`, z.object({ success: z.boolean() }), {
        method: 'DELETE',
      }),
  },

  // Organizations (Phase 5 Batch 2)
  organizations: {
    // Phase 9.13a: Added has_category and has_role parameters for filtering
    list: (params?: { role?: string; search?: string; limit?: number; offset?: number; category_id?: string; has_category?: boolean; has_role?: boolean }) =>
      fetchAPI(
        `/api/organizations?${new URLSearchParams(
          Object.entries(params || {})
            // Phase 7.5.20: Filter out undefined, null, empty strings, and objects
            .filter(([_, v]) => v !== undefined && v !== null && v !== '' && typeof v !== 'object')
            .map(([k, v]) => [k, String(v)])
        )}`,
        organizationListResponseSchema
      ),

    // Phase 7 Batch 1: Search endpoint with relevance ordering
    search: (params: { q: string; role?: string; category_id?: string; limit?: number }) =>
      fetchAPI(
        `/api/organizations/search?${new URLSearchParams(
          Object.entries(params)
            // Phase 7.5.20: Filter out undefined, null, empty strings, and objects
            .filter(([_, v]) => v !== undefined && v !== null && v !== '' && typeof v !== 'object')
            .map(([k, v]) => [k, String(v)])
        )}`,
        organizationListResponseSchema
      ),

    // Phase 7 Batch 1: Recent organizations from project scopes
    getRecent: (params?: { role?: string; category_id?: string; limit?: number }) =>
      fetchAPI(
        `/api/organizations/recent?${new URLSearchParams(
          Object.entries(params || {})
            // Phase 7.5.20: Filter out undefined, null, empty strings, and objects
            .filter(([_, v]) => v !== undefined && v !== null && v !== '' && typeof v !== 'object')
            .map(([k, v]) => [k, String(v)])
        )}`,
        organizationListResponseSchema
      ),

    // Wave 3 v0.14 org-change §5.0: cross-entity picker search. Returns both
    // organizations (kind='org') and organization_establishments (kind='dba')
    // in a single UNION'd response, alphabetical within kind, orgs first when
    // both kinds match. Used by OrgChangeStepper's Step 1 picker.
    //
    // Wave 2 ships the real backend; Wave 3 calls against the spec'd contract
    // (a stub at this path returns the shape during local dev — see WAVE3 NOTES
    // on the OrgChangeStepper component).
    orgChangeSearch: (params: { q: string; include_archived?: boolean; limit?: number }) =>
      fetchAPI(
        `/api/organizations/search-clients?${new URLSearchParams(
          Object.entries(params)
            .filter(([_, v]) => v !== undefined && v !== null && v !== '' && typeof v !== 'object')
            .map(([k, v]) => [k, String(v)])
        )}`,
        orgChangeSearchResponseSchema
      ),

    get: (id: string) =>
      fetchAPI(`/api/organizations/${id}`, z.object({ success: z.boolean(), organization: organizationSchema })),

    create: (data: OrganizationCreate) =>
      fetchAPI(`/api/organizations`, z.object({ success: z.boolean(), organization: organizationSchema }), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: OrganizationUpdate) =>
      fetchAPI(`/api/organizations/${id}`, z.object({ success: z.boolean(), organization: organizationSchema }), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchAPI(`/api/organizations/${id}`, z.object({ success: z.boolean() }), {
        method: 'DELETE',
      }),
  },

  // Organization Contacts (Phase 5 Batch 3)
  organizationContacts: {
    // `generalOnly` is the client page's Contacts list: people who are not client default contacts.
    list: (organizationId: string, opts?: { generalOnly?: boolean }) =>
      fetchAPI(
        `/api/organizations/${organizationId}/contacts${opts?.generalOnly ? '?general_only=true' : ''}`,
        organizationContactListResponseSchema,
      ),

    create: (organizationId: string, data: OrganizationContactCreate) =>
      fetchAPI(`/api/organizations/${organizationId}/contacts`, z.object({ success: z.boolean(), contact: organizationContactSchema }), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (organizationId: string, contactId: string, data: OrganizationContactUpdate) =>
      fetchAPI(`/api/organizations/${organizationId}/contacts/${contactId}`, z.object({ success: z.boolean(), contact: organizationContactSchema }), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (organizationId: string, contactId: string) =>
      fetchAPI(`/api/organizations/${organizationId}/contacts/${contactId}`, z.object({ success: z.boolean() }), {
        method: 'DELETE',
      }),
  },

  // Organization Establishments (Phase 9.18d - DBA Names)
  organizationEstablishments: {
    list: (organizationId: string) =>
      fetchAPI(`/api/organizations/${organizationId}/establishments`, establishmentListResponseSchema),

    create: (organizationId: string, data: EstablishmentCreate) =>
      fetchAPI(`/api/organizations/${organizationId}/establishments`, z.object({ success: z.boolean(), establishment: establishmentSchema }), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (organizationId: string, establishmentId: number, data: EstablishmentUpdate) =>
      fetchAPI(`/api/organizations/${organizationId}/establishments/${establishmentId}`, z.object({ success: z.boolean(), establishment: establishmentSchema }), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (organizationId: string, establishmentId: number) =>
      fetchAPI(`/api/organizations/${organizationId}/establishments/${establishmentId}`, z.object({ success: z.boolean() }), {
        method: 'DELETE',
      }),
  },

  // Accounting Contacts (V100 migration — invoice billing contacts)
  accountingContacts: {
    list: (organizationId: string, establishmentId?: number) =>
      fetchAPI(
        establishmentId
          ? `/api/organizations/${organizationId}/establishments/${establishmentId}/accounting-contacts`
          : `/api/organizations/${organizationId}/accounting-contacts`,
        accountingContactListResponseSchema
      ),

    create: (organizationId: string, data: AccountingContactCreate, establishmentId?: number) =>
      fetchAPI(
        establishmentId
          ? `/api/organizations/${organizationId}/establishments/${establishmentId}/accounting-contacts`
          : `/api/organizations/${organizationId}/accounting-contacts`,
        accountingContactResponseSchema,
        { method: 'POST', body: JSON.stringify(data) }
      ),

    update: (organizationId: string, contactId: string, data: AccountingContactUpdate) =>
      fetchAPI(
        `/api/organizations/${organizationId}/accounting-contacts/${contactId}`,
        accountingContactResponseSchema,
        { method: 'PUT', body: JSON.stringify(data) }
      ),

    // V239: change a default contact's TO/CC role and/or move them up or down. The group comes from the
    // contact row on the server; the body never names it.
    updateRoleOrder: (
      organizationId: string,
      contactId: string,
      data: { accounting_role?: 'to' | 'cc'; move?: 'up' | 'down' },
    ) =>
      fetchAPI(
        `/api/organizations/${organizationId}/accounting-contacts/${contactId}/role-order`,
        accountingContactRoleOrderResponseSchema,
        { method: 'PATCH', body: JSON.stringify(data) }
      ),

    // Projects of this client that have their own saved contacts (the per-project line under the list).
    projectRecipientSets: (organizationId: string) =>
      fetchAPI(
        `/api/organizations/${organizationId}/project-recipient-sets`,
        projectRecipientSetsResponseSchema,
      ),

    delete: (organizationId: string, contactId: string) =>
      fetchAPI(
        `/api/organizations/${organizationId}/accounting-contacts/${contactId}`,
        z.object({ success: z.boolean() }),
        { method: 'DELETE' }
      ),
  },

  // Clients (Backward compatibility: wraps organizations API)
  clients: {
    list: (params?: { limit?: number; offset?: number }) =>
      fetchAPI(
        `/api/clients?${new URLSearchParams(
          Object.entries(params || {})
            .filter(([_, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)])
        )}`,
        clientListResponseSchema
      ),

    get: (id: string) =>
      fetchAPI(`/api/clients/${id}`, z.object({ success: z.boolean(), client: clientSchema })),

    create: (data: ClientCreate) =>
      fetchAPI(`/api/clients`, z.object({ success: z.boolean(), client: clientSchema }), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: ClientUpdate) =>
      fetchAPI(`/api/clients/${id}`, z.object({ success: z.boolean(), client: clientSchema }), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchAPI(`/api/clients/${id}`, z.object({ success: z.boolean() }), {
        method: 'DELETE',
      }),
  },

  // Billable Items
  billableItems: {
    list: (params?: {
      project_id?: string;
      status?: string;
      search?: string;
      created_after?: string;
      created_before?: string;
      limit?: number;
      offset?: number;
    }) =>
      fetchAPI(
        `/api/billable-items?${new URLSearchParams(
          Object.entries(params || {})
            .filter(([_, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)])
        )}`,
        billableItemListResponseSchema
      ),

    get: (id: string) =>
      fetchAPI(
        `/api/billable-items/${id}`,
        z.object({ success: z.boolean(), billable_item: billableItemSchema })
      ),

    create: (data: BillableItemCreate) =>
      fetchAPI(
        `/api/billable-items`,
        z.object({ success: z.boolean(), billable_item: billableItemSchema }),
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      ),

    update: (id: string, data: BillableItemUpdate) =>
      fetchAPI(
        `/api/billable-items/${id}`,
        z.object({ success: z.boolean(), billable_item: billableItemSchema }),
        {
          method: 'PUT',
          body: JSON.stringify(data),
        }
      ),

    delete: (id: string) =>
      fetchAPI(`/api/billable-items/${id}`, z.object({ success: z.boolean() }), {
        method: 'DELETE',
      }),

    // Cancelled-item spec (R1 contract): read-only pre-check before cancelling.
    // Partitions the item's live invoices into blocking (sent/partially_paid/
    // paid/overdue) vs removable (draft/ready). 404 if the item is missing.
    cancelImpact: (id: string) =>
      fetchAPI(
        `/api/billable-items/${id}/cancel-impact`,
        z.object({
          blocked: z.boolean(),
          blocking_invoices: z.array(
            z.object({ invoice_number: z.string(), status: z.string() })
          ),
          removable_invoices: z.array(
            z.object({ invoice_number: z.string(), status: z.string() })
          ),
        })
      ),
  },

  // Invoices
  invoices: {
    previewChange: (intent: InvoiceChangeIntent) =>
      fetchAPI('/api/invoices/change-preview', invoiceChangePlanSchema, {
        method: 'POST', body: JSON.stringify(intent),
      }),
    applyChange: (request: InvoiceChangeApplyRequest) =>
      fetchAPI('/api/invoices/change-apply', invoiceChangeApplyResultSchema, {
        method: 'POST', body: JSON.stringify(request),
      }),
    // SPEC-invoice-merge-history R5 iteration 4: held provider payments (ADV-R5-04) and unconfirmed
    // checkout reservations (ADV-R5-05). z.unknown() + a narrow read: these shapes are server-owned.
    paymentHolds: async (id: string): Promise<HeldPayment[]> => {
      const raw = (await fetchAPI(`/api/invoices/${id}/payment-holds`, z.unknown())) as { holds?: HeldPayment[] } | null;
      return Array.isArray(raw?.holds) ? raw!.holds : [];
    },
    resolvePaymentHold: (id: string, holdId: string, body: { outcome: HoldOutcome; note: string; version: string }) =>
      fetchAPI(`/api/invoices/${id}/payment-holds/${holdId}/resolve`, z.unknown(), {
        method: 'POST', body: JSON.stringify(body),
      }),
    reconcileCheckoutReservation: async (
      id: string, reservationId: string, body: { cancel_open_intent?: boolean } = {},
    ): Promise<ReconcileResult> =>
      (await fetchAPI(`/api/invoices/${id}/checkout-reservations/${reservationId}/reconcile`, z.unknown(), {
        method: 'POST', body: JSON.stringify(body),
      })) as ReconcileResult,
    documentHistory: (id: string) =>
      fetchAPI(`/api/invoices/${id}/document-history`, invoiceDocumentHistoryDataSchema),
    // SPEC-invoice-merge-history R3: paginated, source-labelled history of this invoice and
    // every invoice merged into it.
    lineageHistory: (id: string, cursor?: string | null, limit = 50) =>
      fetchAPI(
        `/api/invoices/${id}/lineage-history?${new URLSearchParams({
          limit: String(limit), ...(cursor ? { cursor } : {}),
        })}`,
        invoiceLineageHistoryPageSchema,
      ),
    assertExternalSend: (id: string, revisionId: string | null) =>
      fetchAPI(`/api/invoices/${id}/external-send-assertion`, z.object({
        success: z.boolean(), event: z.object({ id: z.string(), occurred_at: z.string() }).passthrough(),
      }), { method: 'POST', body: JSON.stringify({ revision_id: revisionId }) }),
    downloadRevision: async (id: string, revisionId: string): Promise<{ blob: Blob; filename: string | null }> => {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/invoices/${id}/document-revisions/${revisionId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 410) {
        // The archive row exists but its bytes were never stored or are gone. Say so; never
        // substitute today's data for the document that was issued.
        throw new DocumentBytesUnavailableError();
      }
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(extractErrorMessage(body, response.statusText));
      }
      return { blob: await response.blob(),
        filename: invoiceDownloadFilename(response.headers.get('Content-Disposition')) };
    },
    // Phase 9.13b: Added sort_by and sort_order parameters
    list: (params?: { project_id?: string; status?: string; search?: string; sort_by?: string; sort_order?: string; limit?: number; offset?: number }) =>
      fetchAPI(
        `/api/invoices?${new URLSearchParams(
          Object.entries(params || {})
            .filter(([_, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)])
        )}`,
        invoiceListResponseSchema
      ),

    // Phase 9.19e: Backend returns invoice_items, establishments, contacts with include_related=true (default)
    get: (id: string) =>
      fetchAPI(`/api/invoices/${id}`, z.object({
        success: z.boolean(),
        invoice: invoiceSchema,
        // Phase 9.19e: Related data (optional for backward compatibility)
        invoice_items: z.array(invoiceItemSchema).optional(),
        establishments: z.array(z.object({
          id: z.number(),
          name: z.string(),
          is_default: z.boolean(),
        })).optional(),
        contacts: z.array(z.object({
          id: z.string(),
          name: z.string(),
          email: z.string().nullable().optional(),
          phone: z.string().nullable().optional(),
          is_primary: z.boolean().nullable().optional(),
        })).optional(),
        // R2/R3: Invoice contacts snapshot (TO/CC) stored in invoice_contacts table.
        // `id` is the invoice_contacts row id — the FE needs it to remove a recipient
        // via DELETE /api/invoices/{id}/contacts/{row_id}. Without this, zod strips
        // the field and the inline × button never renders.
        invoice_contacts: z.array(z.object({
          id: z.string().optional(),
          invoice_id: z.string().optional(),
          contact_id: z.string().nullable().optional(),
          contact_name: z.string(),
          contact_email: z.string(),
          role: z.string(),
          created_at: z.string().optional(),
        })).optional(),
        // bill-to fix 2026-05: Surfaces the silent empty-TO fallback so the BILL TO
        // card can warn the user when the PDF will render the org's Primary contact
        // in the Attention block (or note the line will be blank). Matches the
        // shape computed in routers/invoices.py.
        //
        // V175 (bill-to suppress): suppress_attention_line reflects the per-invoice
        // opt-out flag. When TRUE the PDF renders no Attention line even if
        // fallback_active=true; fallback_contact still echoes what would have
        // fired so the UI can preview "Restore default".
        to_contacts_meta: z.object({
          explicit_count: z.number(),
          fallback_active: z.boolean(),
          fallback_contact: z.object({
            contact_name: z.string(),
            contact_email: z.string(),
          }).nullable(),
          suppress_attention_line: z.boolean().optional().default(false),
        }).optional(),
        // R3a (v0.14): scope_state drives the BILL TO modal's State A/B/C
        // decision when the user changes the invoice's establishment. When
        // is_invoiced=true the FE must drop `save_to_scope` from the modal's
        // availableModes — the backend will return 409 if it slips through.
        scope_state: z.object({
          scope_id: z.string().nullable(),
          scope_establishment_id: z.number().int().nullable(),
          scope_establishment_name: z.string().nullable(),
          is_invoiced: z.boolean(),
          sibling_scope_count: z.number().int(),
          scope_invoice_count_total: z.number().int(),
          scope_invoice_count_sent: z.number().int(),
        }).nullable().optional(),
        // R3a (v0.14): address_state surfaces the resolved source plus sibling
        // counts for the propagation modal copy ("N other invoices on this
        // project use the project source").
        address_state: z.object({
          source: z.string(),
          project_address_set: z.boolean(),
          establishment_address_set: z.boolean(),
          siblings_using_project_source: z.number().int(),
          siblings_using_establishment_source: z.number().int(),
        }).nullable().optional(),
        // R3a (v0.14): legal_name_state — cascade count for the
        // save_to_establishment confirmation copy.
        legal_name_state: z.object({
          source: z.string(),
          establishment_legal_name_set: z.boolean(),
          siblings_using_this_establishment: z.number().int(),
        }).nullable().optional(),
        // R3a (v0.14): recipients_meta — one row per invoice_contacts row with
        // source classification (snapshot_dba/snapshot_org/user_added/typed_in)
        // and DBA/org accounting-flag state. Drives the per-recipient star
        // toggle + remove modal.
        //
        // The `source='user_added'` case can include a dangling FK (deleted
        // org_contact) — name/email may both be null. UI renders "(removed
        // contact)" and omits the star toggle in that case.
        recipients_meta: z.array(z.object({
          invoice_contact_id: z.string().nullable(),
          contact_id: z.string().nullable(),
          name: z.string().nullable(),
          email: z.string().nullable(),
          role: z.string(),
          source: z.enum(['snapshot_dba', 'snapshot_org', 'user_added', 'typed_in']),
          is_dba_accounting: z.boolean(),
          is_org_accounting: z.boolean(),
          // R2 AC13: this person is one of the project's saved people.
          is_project_default: z.boolean().optional(),
          // M2: the invoice's project has people saved of its own, so the client's default contacts do not apply to it.
          project_has_own_people: z.boolean().optional(),
        })).optional(),
        // R4 (invoice-identity-cascade): R2's resolution bundle for THIS invoice,
        // surfaced on the read path so the Christa-Land BILL TO panel can render
        // source tags + the consequence-preview picker + the gate. Each cascade
        // field carries {value, source, suggested, resolved}; the convenience
        // counts (project_draft_count / client_invoice_count / multi_client_
        // project) + last_sent_number drive the picker copy. OPTIONAL — the GET
        // endpoint surfaces it once the operator/R5 wires it (R2 built the
        // engine; this is the read-path projection). Declared here so it
        // survives the Zod boundary (no [Zod-strip] dead field).
        identity_resolution: z.object({
          establishment: z.object({
            value: z.any().optional(),
            source: z.string(),
            suggested: z.boolean(),
            resolved: z.boolean(),
          }).nullable().optional(),
          recipients: z.object({
            value: z.any().optional(),
            source: z.string(),
            suggested: z.boolean(),
            resolved: z.boolean(),
          }).nullable().optional(),
          billing_address: z.object({
            value: z.any().optional(),
            source: z.string(),
            suggested: z.boolean(),
            resolved: z.boolean(),
          }).nullable().optional(),
          default_differs_from_last_sent: z.boolean().optional(),
          any_unresolved: z.boolean().optional(),
          any_suggested: z.boolean().optional(),
          // R4 picker-copy convenience fields (computed by the read projection).
          project_draft_count: z.number().int().optional(),
          client_invoice_count: z.number().int().optional(),
          multi_client_project: z.boolean().optional(),
          last_sent_number: z.string().nullable().optional(),
          // The PROJECT recipient pin, reported as itself. Undeclared keys are
          // stripped by fetchAPI's parse, so omitting this would make the pin
          // invisible to the panel exactly like the write-only stores it exists
          // to surface. `count` is the number of contacts in
          // project_scope_recipient_defaults; `cleared` is V211's deliberate
          // "nobody on this project". null when the invoice has no client scope.
          project_recipient_pin: z.object({
            count: z.number().int(),
            cleared: z.boolean(),
            names: z.array(z.string()).optional(),
          }).nullable().optional(),
        }).nullable().optional(),
        // R2 (SPEC-v017-completion-2026-09-27): "who do I send this invoice
        // to?" — 2026-09-21, inv 0729: "I'm finding that I need to go back
        // through my emails to see who to send these too". She sends from
        // Outlook, so the answer has to be readable + copyable ON the invoice
        // page before she downloads the PDF. `recorded_on_invoice` is the
        // honesty flag: false means the address was INFERRED (project pin,
        // client accounting contacts, the last invoice issued to this client,
        // the client's main contact) and `origin_label` says which. Declared
        // here or fetchAPI's parse strips the whole block and the card never
        // renders.
        recipient_lookup: z.object({
          recipients: z.array(z.object({
            name: z.string().nullable(),
            email: z.string().nullable(),
            role: z.string(),
            missing_email: z.boolean(),
          })),
          origin: z.string(),
          origin_label: z.string(),
          recorded_on_invoice: z.boolean(),
          copy_text: z.string(),
          // QA round 2: TO and CC are carried SEPARATELY. A semicolon-joined
          // blob pastes into Outlook's To field, which turns a person she
          // meant to CC into a visible addressee on a client's invoice email.
          // Optional so a page served by an older backend still parses.
          copy_text_to: z.string().optional(),
          copy_text_cc: z.string().optional(),
          emailable_count: z.number().int(),
          empty_reason: z.string().nullable(),
          // QA round 3: who the ATTACHED PDF will address when this invoice
          // records no TO of its own. A CC-only invoice used to answer "Send
          // this invoice to" with the CC alone while the PDF was addressed to
          // the org Primary -- so pasting the block into Outlook dropped the
          // real addressee. Carried SEPARATELY from `recipients` because it is
          // NOT recorded on the invoice and must never be counted, copied or
          // labelled as if it were. Optional so an older backend still parses.
          to_unresolved: z.object({
            name: z.string().nullable(),
            email: z.string().nullable(),
            origin: z.string(),
            origin_label: z.string(),
          }).nullable().optional(),
        }).nullable().optional(),
      })),

    getNextNumber: () =>
      fetchAPI(`/api/invoices/next-number/suggest`, z.object({ success: z.boolean(), suggested_invoice_number: z.string() })),

    create: (data: InvoiceCreateRequest) =>
      fetchAPI(`/api/invoices`, z.object({
        success: z.boolean(),
        invoice: invoiceSchema,
        // R4: present only on a keyed create. True when this answer is the invoice an EARLIER request with the
        // same operation key created (a retry, a reload or a second tab). Declared here or fetchAPI strips it.
        replayed: z.boolean().optional(),
        // TRUE when the backend created this invoice WITHOUT its inherited
        // identity — no project DBA, no recipients, no billing address —
        // because resolution threw. Declared here because fetchAPI's parse
        // strips undeclared keys, which would make the one signal about a
        // silently-empty invoice invisible: the exact failure shape this whole
        // change set exists to end.
        identity_resolution_failed: z.boolean().optional(),
      }), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    // `confirmDestructiveEntityId` (super-admin destructive-op guard): when set,
    // attaches the X-Super-Admin-Confirm-Destructive header carrying the invoice
    // id to THIS request only. Callers pass it after the super-admin clears the
    // confirm modal on a guarded status downgrade (paid→sent / sent→ready /
    // ready→draft / any→cancelled). Non-super-admins never pass it — and the
    // backend ignores the header for them anyway.
    update: (id: string, data: InvoiceUpdate, confirmDestructiveEntityId?: string) =>
      fetchAPI(`/api/invoices/${id}`, z.object({
        success: z.boolean(),
        invoice: invoiceSchema,
        // v0.12.22 Fix 1: backend signals when project_scopes.establishment_id
        // was just back-filled from a NULL state, so the FE can toast.
        scope_updated: z.boolean().optional(),
        // SPEC-project-po-number-2026-08-17 R2 AC4/4a: True iff this PUT's
        // apply_po_to_project=True actually wrote onto the project (project
        // had no PO). False when the flag was sent but its precondition
        // wasn't met (project already had a PO, or po_number was cleared).
        // Undeclared here would be silently Zod-stripped -- the caller could
        // never tell whether the write-back applied.
        po_write_back_applied: z.boolean().nullable().optional(),
      }), {
        method: 'PUT',
        body: JSON.stringify(data),
        ...(confirmDestructiveEntityId
          ? { headers: superAdminConfirmHeader(confirmDestructiveEntityId) }
          : {}),
      }),

    delete: (id: string) =>
      fetchAPI(`/api/invoices/${id}`, z.object({ success: z.boolean() }), {
        method: 'DELETE',
      }),

    // Cancel-invoice feature: POST /{id}/cancel. `confirmDestructiveEntityId`
    // (super-admin destructive-op guard): a super-admin cancelling a
    // paid/partially_paid/sent/ready invoice on a production tenant is a
    // guarded op -- same pattern as `update`'s status-downgrade header above.
    // was_exported_to_qbo / qbo_csv_exported_at let the dialog warn her to
    // void it in QuickBooks too, without a second round-trip.
    cancel: (id: string, data: { reason?: string }, confirmDestructiveEntityId?: string) =>
      fetchAPI(`/api/invoices/${id}/cancel`, z.object({
        success: z.boolean(),
        invoice: invoiceSchema,
        was_exported_to_qbo: z.boolean(),
        qbo_csv_exported_at: z.string().nullable().optional(),
      }), {
        method: 'POST',
        body: JSON.stringify(data),
        ...(confirmDestructiveEntityId
          ? { headers: superAdminConfirmHeader(confirmDestructiveEntityId) }
          : {}),
      }),

    // SPEC-invoice-merge-2026-08-17 R3: combine two invoices into one.
    // Named for what it does (merge), not "link" -- see R3-merge-ui.md.
    // Refuses 409 with R1's specific reason (routers/invoices.py
    // merge_invoices); the caller (InvoiceListPage) unwraps that via
    // extractMergeRefusal (lib/invoice-merge.ts) since main.py's global
    // exception handler nests the refusal detail under `error`, which
    // fetchAPI's generic error path does not unwrap.
    merge: (data: { invoice_id_a: string; invoice_id_b: string }) =>
      fetchAPI(`/api/invoices/merge`, z.object({
        success: z.boolean(),
        survivor_invoice_id: z.string(),
        survivor_invoice_number: z.string(),
        absorbed_invoice_id: z.string(),
        absorbed_invoice_number: z.string(),
        moved_line_item_count: z.number(),
      }), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    // SPEC-invoice-merge-history-2026-10-02 R1: read-only reviewed-merge plan. Server-side
    // gated by the reviewed_invoice_merge tenant flag and the invoice-edit capability; the
    // strict parser (lib/invoice-merge-plan.ts) rejects any key it does not know.
    mergePreview: (data: MergePreviewRequest) =>
      fetchAPI(`/api/invoices/merge-preview`, mergePreviewResponseSchema, {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    // SPEC-invoice-merge-history R4 AC6: the reviewed apply. The result is parsed strictly HERE and a
    // body that does not match the contract is an UNCONFIRMED outcome (no `status`), never a success:
    // fetchAPI hands back an unparseable body as-is, which would otherwise read as "merged".
    mergeApply: async (data: MergeApplyRequest): Promise<MergeApplyResult> => {
      const raw = await fetchAPI(`/api/invoices/merge-apply`, z.unknown(), {
        method: 'POST',
        body: JSON.stringify(data),
      });
      const parsed = mergeApplyResponseSchema.safeParse(raw);
      if (!parsed.success || parsed.data.success !== true) {
        throw new Error('The server answered the merge request with a response this page does not recognise.');
      }
      return parsed.data.result;
    },

    // SPEC-invoice-merge-history R4 AC8: PDF of the combined invoice exactly as the reviewed plan produces
    // it. Writes nothing and sends nothing server-side; bound to the plan digest, so a changed plan is a
    // 409 here. The digest header is checked so a preview is never shown for a different plan.
    mergePreviewPdf: async (
      data: MergePreviewPdfRequest,
    ): Promise<{ blob: Blob; planDigest: string | null; projectionHash: string | null; filename: string | null }> => {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/invoices/merge-preview-pdf`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throwIf403(response.status, errorData);
        const error: any = new Error(extractErrorMessage(errorData, response.statusText));
        error.status = response.status;
        error.data = errorData;
        throw error;
      }
      return {
        blob: await response.blob(),
        planDigest: response.headers.get('X-Merge-Plan-Digest'),
        projectionHash: response.headers.get('X-Merge-Projection-Hash'),
        filename: invoiceDownloadFilename(response.headers.get('Content-Disposition')),
      };
    },

    // R4 (invoice-identity-cascade) — the unified three-level cascade contract
    // (R3 POST /{id}/cascade). ONE shape drives all four cascade fields
    // (dba/recipients/billing_address/hide_dba) at level ∈ {this_invoice,
    // this_project, this_client}. The FE inline picker maps the chosen level
    // into this body. `applied` is the no-silent-no-op signal (AC8): the
    // backend RAISES on a 0-row write, so a 200 here means the write landed.
    cascadeWrite: (
      id: string,
      data: {
        field: 'dba' | 'recipients' | 'billing_address' | 'hide_dba';
        level: 'this_invoice' | 'this_project' | 'this_client';
        establishment_id?: number | null;
        billing_address_source?: string | null;
        billing_address_override?: string | null;
        hide_dba?: boolean | null;
        contact_id?: string | null;
        recipient_role?: string | null;
        recipient_add?: boolean;
        /**
         * 2026-08-07: the ATOMIC project recipient set. The resolver treats a
         * non-empty project set as THE COMPLETE SET, so pinning it one contact
         * at a time lost roles and could leave a truncated set behind on a
         * partial failure. Send the whole set; an empty array clears it.
         */
        recipient_set?: { contact_id: string; role: 'to' | 'cc' }[];
      },
    ) =>
      fetchAPI(
        `/api/invoices/${id}/cascade`,
        z.object({
          success: z.boolean(),
          field: z.string(),
          level: z.string(),
          applied: z.boolean(),
          target_id: z.string().nullable().optional(),
          // Rows actually STORED by a this_project recipient_set write. The
          // server drops contact_ids whose org contact was deleted, so this can
          // be lower than what we sent — and 0 means the pin was cleared.
          // Undeclared keys are stripped by fetchAPI's Zod parse, so omitting
          // this would make the count invisible to the caller.
          recipients_written: z.number().nullable().optional(),
        // Whether a single-contact project-level recipient write actually
        // changed a row. False means the removal did NOT stick going forward --
        // 311 of 317 scopes carry no pin, so deleting from it is usually a
        // no-op. Undeclared keys are stripped by fetchAPI's parse.
        recipient_default_changed: z.boolean().nullable().optional(),
        }),
        { method: 'POST', body: JSON.stringify(data) },
      ),

    // R4 — retroactive apply to unsent drafts (R3 POST /{id}/cascade/retroactive).
    // Returns the sibling invoice NUMBERS that changed NOW so the panel can
    // render the evidentiary confirmation (AC4). Drafts only; sent rows are
    // R1-frozen and untouched.
    cascadeRetroactive: (
      id: string,
      data: {
        field: string;
        establishment_id?: number | null;
        billing_address_source?: string | null;
        billing_address_override?: string | null;
        hide_dba?: boolean | null;
        picker_opened_at?: string | null;
        scope_to_client_scope?: boolean;
      },
    ) =>
      fetchAPI(
        `/api/invoices/${id}/cascade/retroactive`,
        z.object({
          success: z.boolean(),
          updated_count: z.number(),
          updated_invoice_ids: z.array(z.string()),
          // Sibling invoice NUMBERS that changed (VERBATIM, zero-padded). The
          // backend may not echo these on every build; tolerate absence.
          updated_invoice_numbers: z.array(z.string()).optional(),
        }),
        { method: 'POST', body: JSON.stringify(data) },
      ),

    // R4 — confirm-gated legal-name correction (R3 POST /{id}/legal-name-correction).
    // confirm=false PREVIEWS the org-wide blast radius as a LIST of invoice
    // NUMBERS (AC7b); confirm=true commits. Legal entity is NOT a cascade field
    // — it rides with the DBA.
    legalNameCorrection: (
      id: string,
      data: { new_legal_name: string; confirm: boolean },
    ) =>
      fetchAPI(
        `/api/invoices/${id}/legal-name-correction`,
        z.object({
          success: z.boolean(),
          confirmed: z.boolean(),
          new_legal_name: z.string(),
          affected_count: z.number(),
          affected_invoice_numbers: z.array(z.string()),
        }),
        { method: 'POST', body: JSON.stringify(data) },
      ),

    // R4 — easy in-context add-DBA under the EXISTING client (R3 POST
    // /{id}/add-dba). Plain Christa vocabulary: trade_name shows on the invoice,
    // legal_name is the legal company name. Never mints a new organization.
    addDba: (
      id: string,
      data: {
        trade_name: string;
        legal_name?: string | null;
        hide_dba_default?: boolean;
        select_on_invoice?: boolean;
      },
    ) =>
      fetchAPI(
        `/api/invoices/${id}/add-dba`,
        z.object({
          success: z.boolean(),
          // create_establishment returns a wider row; keep the fields the FE
          // needs (id + name to select it on the invoice) and passthrough the
          // rest so a wider backend shape never Zod-strips a present field.
          establishment: z.object({
            id: z.number(),
            name: z.string(),
            legal_name: z.string().nullable().optional(),
          }).passthrough(),
          selected_on_invoice: z.boolean().optional(),
        }),
        { method: 'POST', body: JSON.stringify(data) },
      ),

    recordPayment: (id: string, data: { amount: number; payment_date: string }) =>
      fetchAPI(`/api/invoices/${id}/payments`, z.object({ success: z.boolean(), invoice: invoiceSchema }), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    // V172 invoice payments ledger: fetch payment rows for an invoice
    getPayments: (invoiceId: string) =>
      fetchAPI(`/api/invoices/${invoiceId}/payments`, z.object({
        success: z.boolean(),
        payments: z.array(z.object({
          id: z.string().uuid(),
          amount: z.number(),
          paid_at: z.string(),
          status: z.enum(['active', 'voided']),
          voided_at: z.string().nullable().optional(),
          voided_by: z.string().nullable().optional(),
          // Theme 6b S2: backend resolves UID -> email for display (recorded_by/voided_by remain UIDs)
          voided_by_email: z.string().nullable().optional(),
          void_reason: z.string().nullable().optional(),
          recorded_by: z.string().nullable().optional(),
          recorded_by_email: z.string().nullable().optional(),
          created_at: z.string(),
          // SPEC-invoice-merge-history R3: these were Zod-stripped before, so a merged
          // invoice's payment screen could not say where a receipt was first recorded.
          method: z.string().nullable().optional(),
          reference: z.string().nullable().optional(),
          provider: z.string().nullable().optional(),
          provider_payment_intent_id: z.string().nullable().optional(),
          original_invoice_id: z.string().nullable().optional(),
          original_invoice_number: z.string().nullable().optional(),
          original_invoice_provenance: z.string().nullable().optional(),
          invoice_id: z.string().optional(),
          current_invoice_number: z.string().nullable().optional(),
        })),
        requested_invoice_id: z.string().nullable().optional(),
        current_invoice_id: z.string().nullable().optional(),
        is_merged_alias: z.boolean().optional(),
      })),

    // Phase 6 void payment: Backend returns minimal invoice (RETURNING clause subset)
    // Use partial schema to avoid Zod validation errors on missing join fields (organization_id, etc.)
    // V172: updated to accept { void_reason, payment_id? } instead of { amount }
    // `confirmDestructiveEntityId` (super-admin destructive-op guard): voiding a
    // payment is a guarded destructive op (the May 2026 incident's chain link).
    // When set, attaches X-Super-Admin-Confirm-Destructive: <invoice id> to THIS
    // request only, after the super-admin confirms the void. The id MUST be the
    // invoice id (the entity the backend guards), not the payment id.
    voidPayment: (id: string, data: { void_reason: string; payment_id?: string }, confirmDestructiveEntityId?: string) =>
      fetchAPI(`/api/invoices/${id}/payments`, z.object({
        success: z.boolean(),
        invoice: z.object({
          id: z.string().uuid(),
          invoice_number: z.string(),
          project_id: z.string().uuid(),
          invoice_date: z.string().nullable().optional(),
          due_date: z.string().nullable().optional(),
          fee_amount: z.number().nullable().optional(),
          tax_amount: z.number().nullable().optional(),
          total_amount: z.number(),
          amount_paid: z.number().nullable().optional(),
          status: z.string(),
          payment_date: z.string().nullable().optional(),
          status_changed_at: z.string().nullable().optional(),
          status_changed_by: z.string().nullable().optional(),
          previous_status: z.string().nullable().optional(),
          created_at: z.string(),
          updated_at: z.string(),
        }),
      }), {
        method: 'DELETE',
        body: JSON.stringify(data),
        ...(confirmDestructiveEntityId
          ? { headers: superAdminConfirmHeader(confirmDestructiveEntityId) }
          : {}),
      }),

    downloadPDF: async (id: string, forceRegenerate: boolean = false): Promise<{ blob: Blob; filename: string | null }> => {
      const token = await getAuthToken();
      const url = `${API_BASE_URL}/api/invoices/${id}/pdf/download${forceRegenerate ? '?force_regenerate=true' : ''}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const rawMessage = errorData.detail || errorData.error || errorData.message || response.statusText || 'Unknown error';
        const errorMessage = typeof rawMessage === 'string' ? rawMessage : JSON.stringify(rawMessage);
        const error: any = new Error(errorMessage);
        error.status = response.status;
        error.data = errorData;
        throw error;
      }

      return {
        blob: await response.blob(),
        filename: invoiceDownloadFilename(response.headers.get('Content-Disposition')),
      };
    },

    /**
     * R3 AC10: the PDF as it would print now, for LOOKING at it. Read-only: the server stores nothing, raises no
     * `pdf_version`, saves no document revision and queues no Dropbox upload. Use `downloadPDF` for a real download.
     */
    previewPDF: async (id: string): Promise<{ blob: Blob; filename: string | null }> => {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/invoices/${id}/pdf/preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const rawMessage = errorData.detail || errorData.error || errorData.message || response.statusText || 'Unknown error';
        const errorMessage = typeof rawMessage === 'string' ? rawMessage : JSON.stringify(rawMessage);
        const error: any = new Error(errorMessage);
        error.status = response.status;
        error.data = errorData;
        throw error;
      }

      return {
        blob: await response.blob(),
        filename: invoiceDownloadFilename(response.headers.get('Content-Disposition')),
      };
    },

    getBillingQueue: () =>
      fetchAPI(`/api/invoices/billing-queue`, billingQueueResponseSchema),

    batchUpdateDate: (data: { invoice_ids: string[]; invoice_date: string }) =>
      fetchAPI(`/api/invoices/batch`, z.object({
        success: z.boolean(),
        updated_ids: z.array(z.string()),
        errors: z.array(z.object({ id: z.string(), error: z.string() })),
        updated_count: z.number(),
      }), {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    getResolvedAccountingContacts: (invoiceId: string) =>
      fetchAPI(
        `/api/invoices/${invoiceId}/resolved-accounting-contacts`,
        z.object({
          success: z.boolean(),
          contacts: z.array(accountingContactSchema),
          count: z.number(),
        })
      ),

    refreshContacts: (invoiceId: string) =>
      fetchAPI(
        `/api/invoices/${invoiceId}/refresh-contacts`,
        z.object({
          success: z.boolean(),
          invoice_contacts: z.array(z.object({
            id: z.string().optional(),
            invoice_id: z.string().optional(),
            contact_id: z.string().nullable().optional(),
            contact_name: z.string(),
            contact_email: z.string(),
            role: z.string(),
            created_at: z.string().optional(),
          })),
          count: z.number(),
        }),
        { method: 'POST' }
      ),

    getEligibleContacts: (invoiceId: string) =>
      fetchAPI(
        `/api/invoices/${invoiceId}/eligible-contacts`,
        z.object({
          success: z.boolean(),
          // R1 (hotfix v0.14.5): three grouped buckets, each a TYPED element
          // (no z.any() — that silently strips fields). Element shape matches
          // the real organization_contacts row the picker needs:
          //  - emails is an ARRAY (the canonical multi-email column); `email`
          //    is the legacy scalar kept as a fallback for older rows.
          //  - is_accounting_contact discriminates the accounting pool from the
          //    general "other contacts" pool.
          //  - source: 'establishment' | 'organization' | 'general'.
          establishment_contacts: z.array(eligibleContactSchema),
          org_contacts: z.array(eligibleContactSchema),
          // general_contacts is the previously-invisible non-accounting pool
          // (Christa's 4 general contacts). Optional+default so the client is
          // resilient if it ever talks to an older backend mid-deploy.
          general_contacts: z.array(eligibleContactSchema).optional().default([]),
        })
      ),

    addContact: (
      invoiceId: string,
      data: {
        contact_id?: string;
        contact_name?: string;
        contact_email?: string;
        // R1 (hotfix v0.14.5, AC8): role is optional. Omit it to let the
        // backend auto-assign (first recipient with no TO → 'to', else 'cc').
        // The picker's +TO/+CC buttons send an explicit role; BillToPanel's
        // plain "Add" omits it so the first add lands a TO.
        role?: 'to' | 'cc';
        // R3a (v0.14): controls whether the add ALSO flips
        // is_accounting_contact on the underlying org_contact.
        //   - 'invoice_only'     → invoice_contacts row only (default)
        //   - 'save_dba_default' → also flag accounting at DBA scope
        //   - 'save_org_default' → also flag accounting at org scope
        //   - 'save_project_default' (SPEC-christa-contacts-identity R1) → ONE request that also saves the
        //     person, with the ticked people, for THIS project only
        field_update_mode?: 'invoice_only' | 'save_dba_default' | 'save_org_default' | 'save_project_default';
        // 'save_project_default' only: the saved people on this invoice to keep with the person being added.
        project_set_contact_ids?: string[];
        // 'save_project_default' only (R2 AC14): typed-in rows on this invoice that were ticked to be saved too.
        project_set_row_ids?: string[];
      }
    ) =>
      fetchAPI(
        `/api/invoices/${invoiceId}/contacts`,
        z.object({
          success: z.boolean(),
          invoice_contact: z.object({
            id: z.string(),
            invoice_id: z.string().optional(),
            contact_id: z.string().nullable().optional(),
            contact_name: z.string(),
            contact_email: z.string(),
            role: z.string(),
            created_at: z.string().optional(),
          }),
          propagation_result: z.any().nullable().optional(),
          org_contact_created: z.boolean().optional(),
          contact_id: z.string().nullable().optional(),
        }),
        { method: 'POST', body: JSON.stringify(data) }
      ),

    updateContact: (
      invoiceId: string,
      rowId: string,
      // R2 AC13: `also_project_default` changes the person's role in the project's saved people in the same request.
      data: { contact_name?: string; contact_email?: string; role?: 'to' | 'cc'; also_project_default?: boolean }
    ) =>
      fetchAPI(
        `/api/invoices/${invoiceId}/contacts/${rowId}`,
        z.object({
          success: z.boolean(),
          invoice_contact: z.object({
            id: z.string(),
            invoice_id: z.string().optional(),
            contact_id: z.string().nullable().optional(),
            contact_name: z.string(),
            contact_email: z.string(),
            role: z.string(),
            created_at: z.string().optional(),
          }),
          project_default: z.object({
            applied: z.boolean(),
            client_scope_id: z.string(),
            role: z.string(),
          }).nullable().optional(),
        }),
        { method: 'PATCH', body: JSON.stringify(data) }
      ),

    removeContact: (
      invoiceId: string,
      rowId: string,
      // R3a (v0.14): controls whether the delete ALSO flips
      // is_accounting_contact OFF on the underlying org_contact.
      //   - 'invoice_only' (default): only the invoice_contacts row is removed
      //   - 'stop_for_dba'          : also clear accounting flag at DBA scope
      //   - 'stop_for_org'          : also clear accounting flag org-wide
      fieldUpdateMode?: 'invoice_only' | 'stop_for_dba' | 'stop_for_org',
    ) => {
      const qs = fieldUpdateMode
        ? `?field_update_mode=${encodeURIComponent(fieldUpdateMode)}`
        : '';
      return fetchAPI(
        `/api/invoices/${invoiceId}/contacts/${rowId}${qs}`,
        z.object({
          success: z.boolean(),
          propagation_result: z.any().nullable().optional(),
        }),
        { method: 'DELETE' }
      );
    },

    // R3a (v0.14): set/clear is_accounting_contact on an org_contact in one
    // step. Drives the per-recipient star toggle in the BILL TO list.
    // scope='dba' writes establishment_id = invoice.establishment_id;
    // scope='org' clears establishment_id (only when toggling ON — see backend).
    toggleRecipientDefault: (
      invoiceId: string,
      contactId: string,
      data: { toggle_to: boolean; scope: 'dba' | 'org' },
    ) =>
      fetchAPI(
        `/api/invoices/${invoiceId}/contacts/${contactId}/toggle-default`,
        z.object({
          success: z.boolean(),
          recipients_meta_row: z.any().nullable().optional(),
        }),
        { method: 'POST', body: JSON.stringify(data) }
      ),

    // R3 (SPEC-invoice-send-safety-2026-08-17): promote a `typed_in`
    // recipient (invoice_contacts.contact_id IS NULL) to a saved contact on
    // the invoice's client, IN PLACE — the row itself is updated, never
    // deleted and recreated. No body: the backend decides establishment
    // scope deliberately (AC6) rather than asking.
    promoteContact: (
      invoiceId: string,
      rowId: string,
      // R2 AC14: with `target: 'project'` the person (and any other ticked one-offs) is saved for THIS project
      // instead of becoming a client default. No body is the old "Save to client".
      body?: { target: 'project'; project_set_contact_ids?: string[]; project_set_row_ids?: string[] },
    ) =>
      fetchAPI(
        `/api/invoices/${invoiceId}/contacts/${rowId}/promote`,
        z.object({
          success: z.boolean(),
          invoice_contact: z.object({
            id: z.string(),
            invoice_id: z.string().optional(),
            contact_id: z.string().nullable().optional(),
            contact_name: z.string(),
            contact_email: z.string(),
            role: z.string(),
            created_at: z.string().optional(),
          }),
          recipient_meta: z.any().nullable().optional(),
          org_contact_created: z.boolean().optional(),
          scope: z.string().optional(),
          propagation_result: z.any().nullable().optional(),
          promoted_row_ids: z.array(z.string()).optional(),
        }),
        body ? { method: 'POST', body: JSON.stringify(body) } : { method: 'POST' }
      ),
  },

  // Phase 11a: Schedule of Values
  sov: {
    get: (projectId: string) =>
      fetchAPI(`/api/projects/${projectId}/sov`, z.object({ success: z.boolean(), sov: z.any() })),

    create: (projectId: string, data: unknown) =>
      fetchAPI(`/api/projects/${projectId}/sov`, z.object({ success: z.boolean(), sov: z.any() }), {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  // Phase 11a: Draw Amounts
  drawAmounts: {
    get: (invoiceId: string) =>
      fetchAPI(`/api/invoices/${invoiceId}/draw-amounts`, z.object({ success: z.boolean(), draw_amounts: z.any() })),

    create: (invoiceId: string, data: unknown) =>
      fetchAPI(`/api/invoices/${invoiceId}/draw-amounts`, z.object({ success: z.boolean(), draw_amounts: z.any() }), {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  // R2: Change Orders
  changeOrders: {
    list: (projectId: string) =>
      fetchAPI(`/api/projects/${projectId}/change-orders`, z.object({ success: z.boolean(), change_orders: z.any(), total: z.number() })),

    get: (coId: string) =>
      fetchAPI(`/api/change-orders/${coId}`, z.object({ success: z.boolean(), change_order: z.any() })),

    impact: (projectId: string) =>
      fetchAPI(`/api/projects/${projectId}/change-orders/impact`, z.object({ success: z.boolean(), impact: z.any() })),

    create: (projectId: string, data: unknown) =>
      fetchAPI(`/api/projects/${projectId}/change-orders`, z.object({ success: z.boolean(), change_order: z.any() }), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (coId: string, data: unknown) =>
      fetchAPI(`/api/change-orders/${coId}`, z.object({ success: z.boolean(), change_order: z.any() }), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    submit: (coId: string) =>
      fetchAPI(`/api/change-orders/${coId}/submit`, z.object({ success: z.boolean(), change_order: z.any() }), {
        method: 'POST',
      }),

    approve: (coId: string, data?: unknown) =>
      fetchAPI(`/api/change-orders/${coId}/approve`, z.object({ success: z.boolean(), change_order: z.any() }), {
        method: 'POST',
        body: JSON.stringify(data ?? {}),
      }),

    reject: (coId: string, data?: unknown) =>
      fetchAPI(`/api/change-orders/${coId}/reject`, z.object({ success: z.boolean(), change_order: z.any() }), {
        method: 'POST',
        body: JSON.stringify(data ?? {}),
      }),

    pdfUrl: (coId: string) => `/api/change-orders/${coId}/pdf`,
  },

  // Invoice Items
  invoiceItems: {
    list: (invoiceId: string) =>
      fetchAPI(`/api/invoices/${invoiceId}/items`, invoiceItemListResponseSchema),

    create: (data: InvoiceItemCreate) =>
      fetchAPI(
        `/api/invoice-items`,
        z.object({ success: z.boolean(), invoice_item: z.any() }),
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      ),

    update: (id: string, data: Partial<InvoiceItemCreate> & { lower_billable_item_total?: boolean }) =>
      fetchAPI(
        `/api/invoice-items/${id}`,
        z.object({ success: z.boolean(), invoice_item: z.any() }),
        {
          method: 'PUT',
          body: JSON.stringify(data),
        }
      ),

    delete: (id: string) =>
      fetchAPI(`/api/invoice-items/${id}`, z.object({ success: z.boolean() }), {
        method: 'DELETE',
      }),
  },

  // Dashboard
  dashboard: {
    getPreferences: () =>
      fetchAPI(
        `/api/dashboard/preferences`,
        z.object({ success: z.boolean().optional(), preferences: dashboardPreferencesSchema })
      ).then((res) => res.preferences),

    updatePreferences: (preferences: Partial<DashboardPreferences>) =>
      fetchAPI(`/api/dashboard/preferences`, z.object({ success: z.boolean() }), {
        method: 'PUT',
        body: JSON.stringify(preferences),
      }),

    toggleWidget: (widgetName: string, enabled: boolean) =>
      fetchAPI(
        `/api/dashboard/preferences/widget/${widgetName}?enabled=${enabled}`,
        z.object({ success: z.boolean() }),
        {
          method: 'PATCH',
        }
      ),
  },

  // User Profile
  profile: {
    update: (data: { name: string }) =>
      fetchAPI(`/api/me/profile`, z.object({ success: z.boolean(), message: z.string(), name: z.string() }), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
  },

  // Settings (Phase 6 - Settings Page)
  settings: {
    // Organization Settings
    getOrganizationSettings: () =>
      fetchAPI<OrganizationSettings>(`/api/me/settings/organization`, organizationSettingsSchema),

    updateOrganizationSettings: (data: Partial<OrganizationSettings>) =>
      fetchAPI<SettingsResponse>(`/api/me/settings/organization`, settingsResponseSchema, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    // Invoice Defaults
    getInvoiceDefaults: () =>
      fetchAPI<InvoiceDefaults>(`/api/me/settings/invoice-defaults`, invoiceDefaultsSchema),

    updateInvoiceDefaults: (data: Partial<InvoiceDefaults>) =>
      fetchAPI<SettingsResponse>(`/api/me/settings/invoice-defaults`, settingsResponseSchema, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    // Branding Settings
    getBrandingSettings: () =>
      fetchAPI<BrandingSettings>(`/api/me/settings/branding`, brandingSettingsSchema),

    updateBrandingSettings: (data: Partial<BrandingSettings>) =>
      fetchAPI<SettingsResponse>(`/api/me/settings/branding`, settingsResponseSchema, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    // Proposal Defaults (Phase 8.3 - Proposal PDF Generation)
    getProposalDefaults: () =>
      fetchAPI<ProposalDefaults>(`/api/me/settings/proposal-defaults`, proposalDefaultsSchema),

    updateProposalDefaults: (data: Partial<ProposalDefaults>) =>
      fetchAPI<SettingsResponse>(`/api/me/settings/proposal-defaults`, settingsResponseSchema, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    // Site Visit Defaults — configurable field review statuses
    getSiteVisitDefaults: () =>
      fetchAPI<SiteVisitDefaults>(`/api/me/settings/site-visit-defaults`, siteVisitDefaultsSchema),

    updateSiteVisitDefaults: (data: Partial<SiteVisitDefaults>) =>
      fetchAPI<SettingsResponse>(`/api/me/settings/site-visit-defaults`, settingsResponseSchema, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    // Email Settings (Phase 9.1a - Email Integration)
    getEmailSettings: () =>
      fetchAPI<EmailSettings>(`/api/me/settings/email`, emailSettingsSchema),

    updateEmailSettings: (data: Partial<EmailSettings>) =>
      fetchAPI<SettingsResponse>(`/api/me/settings/email`, settingsResponseSchema, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    // Financial Settings (Phase 9.5b - Revenue Goal Widget)
    getFinancialSettings: () =>
      fetchAPI<FinancialSettings>(`/api/me/settings/financial`, financialSettingsSchema),

    updateFinancialSettings: (data: Partial<FinancialSettings>) =>
      fetchAPI<SettingsResponse>(`/api/me/settings/financial`, settingsResponseSchema, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    // Default Currency (Phase 10a.9 - Multi-currency invoice support)
    getDefaultCurrency: () =>
      fetchAPI<DefaultCurrencySettings>(`/api/me/settings/default-currency`, defaultCurrencySettingsSchema),

    updateDefaultCurrency: (data: DefaultCurrencySettings) =>
      fetchAPI<SettingsResponse>(`/api/me/settings/default-currency`, settingsResponseSchema, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    // Proposal Config (Phase 10c.1 - Proposal terminology customization)
    getProposalConfig: () =>
      fetchAPI<ProposalConfig>(`/api/me/settings/proposal-config`, proposalConfigSchema),

    updateProposalConfig: (data: Partial<ProposalConfig>) =>
      fetchAPI<SettingsResponse>(`/api/me/settings/proposal-config`, settingsResponseSchema, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    // Operational Settings (B4 - Dynamic overdue threshold from Cloud SQL tenant_settings)
    getOperationalSettings: () =>
      fetchAPI<OperationalSettings>(`/api/me/settings/operational`, operationalSettingsSchema),

    // Tax Settings (GST remittance + corp tax estimate dashboard widgets)
    getTaxSettings: () =>
      fetchAPI<TaxSettings>(`/api/me/settings/tax`, taxSettingsSchema),

    updateTaxSettings: (data: Partial<TaxSettings>) =>
      fetchAPI<SettingsResponse>(`/api/me/settings/tax`, settingsResponseSchema, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    // Tax engine status (v0.17 taxclass-label fix, SPEC-bc-pst-tax-engine-2026-08-04):
    // read-only tax_engine mode + rule enablement, so tax-class labels can stop
    // claiming a tax applies when it does not. No PUT — rule mutation is out of
    // this ring's scope.
    getTaxEngineStatus: () =>
      fetchAPI<TaxEngineStatus>(`/api/me/settings/tax-engine`, taxEngineStatusSchema),

    // F1: Document-review tenant settings (in-canvas stamp tool gate).
    getDocumentReviewSettings: () =>
      fetchAPI<{ in_canvas_stamps_enabled: boolean }>(
        `/api/me/settings/document-review`,
        z.any()
      ),

    updateDocumentReviewSettings: (data: { in_canvas_stamps_enabled: boolean }) =>
      fetchAPI<{ success: boolean; settings: { in_canvas_stamps_enabled: boolean } }>(
        `/api/me/settings/document-review`,
        z.any(),
        { method: 'PUT', body: JSON.stringify(data) }
      ),

    // Project List Colours (SPEC-project-list-colour-rules / R2) — per-tenant
    // rules config consumed client-side by the project-list colouring engine.
    // Default (no doc) is {version:1, rules:[]} so the feature is inert.
    getProjectListColors: () =>
      fetchAPI<ProjectListColorsConfig>(`/api/me/settings/project-list-colors`, z.any()),

    // R3: Full-replace PUT of the rules config. Always send the COMPLETE rules
    // array (the backend replaces the stored doc wholesale — there is no patch).
    updateProjectListColors: (data: ProjectListColorsConfig) =>
      fetchAPI<ProjectListColorsConfig>(`/api/me/settings/project-list-colors`, z.any(), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    // Portal Theme (portal_management feature flag required)
    getTheme: () =>
      fetchAPI<Record<string, unknown>>(`/api/me/settings/theme`, z.any()),

    updateTheme: (data: Record<string, unknown>) =>
      fetchAPI<{ success: boolean; theme: Record<string, unknown> }>(
        `/api/me/settings/theme`,
        z.any(),
        { method: 'PUT', body: JSON.stringify(data) }
      ),

    uploadThemeLogo: async (file: File): Promise<{ url: string }> => {
      const token = await getAuthToken();
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_BASE_URL}/api/me/settings/theme/logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error((err as { detail?: string }).detail || 'Logo upload failed');
      }
      return response.json() as Promise<{ url: string }>;
    },
  },

  // Permits (Phase 6 - Permits tracking)
  permits: {
    // Phase 9.16b: Standalone permit list across all projects
    listAll: (options: AllPermitsQueryOptions = {}) => {
      const params = new URLSearchParams();
      if (options.search) params.set('search', options.search);
      if (options.permitTypeDefinitionId) params.set('permit_type_definition_id', options.permitTypeDefinitionId);
      if (options.status) params.set('status', options.status);
      if (options.dueBefore) params.set('due_before', options.dueBefore);
      if (options.dueAfter) params.set('due_after', options.dueAfter);
      if (options.limit) params.set('limit', String(options.limit));
      if (options.offset) params.set('offset', String(options.offset));
      if (options.sortBy) params.set('sort_by', options.sortBy);
      if (options.sortOrder) params.set('sort_order', options.sortOrder);

      const queryString = params.toString();
      const url = `/api/permits${queryString ? `?${queryString}` : ''}`;
      return fetchAPI<AllPermitsResponse>(url, z.any());
    },

    list: (projectId: string) =>
      fetchAPI<ListPermitsResponse>(`/api/projects/${projectId}/permits`, z.any()),

    get: (permitId: string) =>
      fetchAPI<PermitResponse>(`/api/permits/${permitId}`, z.any()),

    create: (projectId: string, data: PermitCreate) =>
      fetchAPI<PermitResponse>(`/api/projects/${projectId}/permits`, z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (permitId: string, data: PermitUpdate) =>
      fetchAPI<PermitResponse>(`/api/permits/${permitId}`, z.any(), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (permitId: string) =>
      fetchAPI<DeletePermitResponse>(`/api/permits/${permitId}`, z.any(), {
        method: 'DELETE',
      }),

    // Phase 8.2: Permit renewal
    renew: (projectId: string, permitId: string) =>
      fetchAPI<PermitResponse>(`/api/projects/${projectId}/permits/${permitId}/renew`, z.any(), {
        method: 'POST',
      }),
  },

  // Phase 9.16a: Permit Type Definitions
  permitTypes: {
    list: (options: { includeInactive?: boolean; category?: string } = {}) => {
      const params = new URLSearchParams();
      if (options.includeInactive) params.set('include_inactive', 'true');
      if (options.category) params.set('category', options.category);
      const queryString = params.toString();
      const url = `/api/permit-types${queryString ? `?${queryString}` : ''}`;
      return fetchAPI<PermitTypesResponse>(url, z.any());
    },

    get: (definitionId: string) =>
      fetchAPI<{ success: boolean; permit_type: PermitTypeDefinition }>(
        `/api/permit-types/${definitionId}`,
        z.any()
      ),
  },

  // Phase 7 Batch 7.3.3: Fee Assumptions Library
  feeAssumptions: {
    list: () =>
      fetchAPI(`/api/fee-assumptions`, z.object({
        success: z.boolean(),
        fee_assumptions: z.array(z.object({
          id: z.string(),
          assumption_text: z.string(),
          display_order: z.number(),
          created_at: z.string(),
          updated_at: z.string(),
        })),
        total: z.number(),
        limit: z.number(),
        offset: z.number(),
      })),

    create: (data: { assumption_text: string }) =>
      fetchAPI(`/api/fee-assumptions`, z.object({
        success: z.boolean(),
        fee_assumption: z.object({
          id: z.string(),
          assumption_text: z.string(),
          display_order: z.number(),
          created_at: z.string(),
          updated_at: z.string(),
        }),
      }), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: { assumption_text: string }) =>
      fetchAPI(`/api/fee-assumptions/${id}`, z.object({
        success: z.boolean(),
        fee_assumption: z.object({
          id: z.string(),
          assumption_text: z.string(),
          display_order: z.number(),
          created_at: z.string(),
          updated_at: z.string(),
        }),
      }), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchAPI(`/api/fee-assumptions/${id}`, z.object({
        success: z.boolean(),
      }), {
        method: 'DELETE',
      }),
  },

  // 9.9d CONTRACT-03: Project Fee Assumptions (per-project, reorderable list)
  projectFeeAssumptions: {
    list: (projectId: string) =>
      fetchAPI<ProjectFeeAssumptionListResponse>(
        `/api/projects/${projectId}/fee-assumptions`,
        projectFeeAssumptionListResponseSchema
      ),

    get: (projectId: string, assumptionId: string) =>
      fetchAPI<ProjectFeeAssumptionResponse>(
        `/api/projects/${projectId}/fee-assumptions/${assumptionId}`,
        projectFeeAssumptionResponseSchema
      ),

    create: (projectId: string, data: ProjectFeeAssumptionCreate) =>
      fetchAPI<ProjectFeeAssumptionResponse>(
        `/api/projects/${projectId}/fee-assumptions`,
        projectFeeAssumptionResponseSchema,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      ),

    update: (projectId: string, assumptionId: string, data: ProjectFeeAssumptionUpdate) =>
      fetchAPI<ProjectFeeAssumptionResponse>(
        `/api/projects/${projectId}/fee-assumptions/${assumptionId}`,
        projectFeeAssumptionResponseSchema,
        {
          method: 'PUT',
          body: JSON.stringify(data),
        }
      ),

    delete: (projectId: string, assumptionId: string) =>
      fetchAPI<{ success: boolean }>(
        `/api/projects/${projectId}/fee-assumptions/${assumptionId}`,
        z.object({ success: z.boolean() }),
        {
          method: 'DELETE',
        }
      ),

    reorder: (projectId: string, items: ProjectFeeAssumptionReorderItem[]) =>
      fetchAPI<ProjectFeeAssumptionListResponse>(
        `/api/projects/${projectId}/fee-assumptions/reorder`,
        projectFeeAssumptionListResponseSchema,
        {
          method: 'POST',
          body: JSON.stringify({ order: items }),
        }
      ),
  },

  // Phase 8 Batch 8.6: Billable Item Templates Library
  billableItemTemplates: {
    list: () =>
      fetchAPI(`/api/billable-item-templates`, z.object({
        success: z.boolean(),
        templates: z.array(z.object({
          id: z.string(),
          tenant_id: z.string(),
          description: z.string(),
          suggested_amount: z.union([z.number(), z.string()]).transform(val => Number(val)),
          deliverables: z.string().nullable(),
          category: z.enum(['professional_fee', 'add_service', 'billable_expense', 'pass_through', 'internal_work']),
          display_order: z.number(),
          is_active: z.boolean(),
          created_at: z.string(),
          updated_at: z.string(),
        })),
        total: z.number(),
        limit: z.number(),
        offset: z.number(),
      })),

    create: (data: {
      description: string;
      suggested_amount: number;
      deliverables?: string | null;
      category: string;
      display_order?: number;
    }) =>
      fetchAPI(`/api/billable-item-templates`, z.object({
        success: z.boolean(),
        template: z.object({
          id: z.string(),
          tenant_id: z.string(),
          description: z.string(),
          suggested_amount: z.union([z.number(), z.string()]).transform(val => Number(val)),
          deliverables: z.string().nullable(),
          category: z.enum(['professional_fee', 'add_service', 'billable_expense', 'pass_through', 'internal_work']),
          display_order: z.number(),
          is_active: z.boolean(),
          created_at: z.string(),
          updated_at: z.string(),
        }),
      }), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: {
      description?: string;
      suggested_amount?: number;
      deliverables?: string | null;
      category?: string;
      display_order?: number;
      is_active?: boolean;
    }) =>
      fetchAPI(`/api/billable-item-templates/${id}`, z.object({
        success: z.boolean(),
        template: z.object({
          id: z.string(),
          tenant_id: z.string(),
          description: z.string(),
          suggested_amount: z.union([z.number(), z.string()]).transform(val => Number(val)),
          deliverables: z.string().nullable(),
          category: z.enum(['professional_fee', 'add_service', 'billable_expense', 'pass_through', 'internal_work']),
          display_order: z.number(),
          is_active: z.boolean(),
          created_at: z.string(),
          updated_at: z.string(),
        }),
      }), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchAPI(`/api/billable-item-templates/${id}`, z.object({
        success: z.boolean(),
      }), {
        method: 'DELETE',
      }),
  },

  // User Views (Phase 10a.9)
  views: {
    list: (params?: { pinned_only?: boolean }) =>
      fetchAPI(
        `/api/views?${new URLSearchParams(
          Object.entries(params || {})
            .filter(([_, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)])
        )}`,
        userViewListResponseSchema
      ),

    get: (id: string) =>
      fetchAPI(`/api/views/${id}`, z.object({
        success: z.boolean(),
        view: userViewSchema,
      })),

    create: (data: { name: string; view_type: string; config: Record<string, any>; icon?: string; position?: number; pinned?: boolean; user_id?: string }) =>
      fetchAPI(`/api/views`, z.object({ success: z.boolean(), view: userViewSchema }), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: Record<string, any>) =>
      fetchAPI(`/api/views/${id}`, z.object({ success: z.boolean(), view: userViewSchema }), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchAPI(`/api/views/${id}`, z.object({ success: z.boolean() }), {
        method: 'DELETE',
      }),
  },

  // Deliverables (Phase 10b.7 - Deliverable Tracking)
  deliverables: {
    list: (projectId: string) =>
      fetchAPI<{ success: boolean; deliverables: any[]; total: number; project_id: string }>(
        `/api/projects/${projectId}/deliverables`, z.any()),

    summary: (projectId: string) =>
      fetchAPI<{ success: boolean; summary: Record<string, number>; project_id: string }>(
        `/api/projects/${projectId}/deliverables/summary`, z.any()),

    get: (deliverableId: string) =>
      fetchAPI<{ success: boolean; deliverable: any }>(
        `/api/deliverables/${deliverableId}`, z.any()),

    create: (projectId: string, data: { title: string; description?: string; status?: string; assigned_to?: string; due_date?: string; sort_order?: number; billable_item_id?: string; scope_id?: string; reviewer?: string; submission_id?: string }) =>
      fetchAPI<{ success: boolean; deliverable: any }>(
        `/api/projects/${projectId}/deliverables`, z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (deliverableId: string, data: Record<string, any>) =>
      fetchAPI<{ success: boolean; deliverable: any }>(
        `/api/deliverables/${deliverableId}`, z.any(), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (deliverableId: string) =>
      fetchAPI<{ success: boolean }>(
        `/api/deliverables/${deliverableId}`, z.any(), {
        method: 'DELETE',
      }),

    reorder: (projectId: string, deliverableIds: string[]) =>
      fetchAPI<{ success: boolean; deliverables: any[]; total: number }>(
        `/api/projects/${projectId}/deliverables/reorder`, z.any(), {
        method: 'PUT',
        body: JSON.stringify(deliverableIds),
      }),

    stale: (days: number = 3) =>
      fetchAPI<{ success: boolean; deliverables: any[]; total: number; stale_threshold_days: number }>(
        `/api/deliverables/stale?days=${days}`, z.any()),

    dashboard: (params?: { assigned_to?: string; reviewer?: string; search?: string }) => {
      const searchParams = new URLSearchParams();
      if (params?.assigned_to) searchParams.set('assigned_to', params.assigned_to);
      if (params?.reviewer) searchParams.set('reviewer', params.reviewer);
      if (params?.search) searchParams.set('search', params.search);
      const qs = searchParams.toString();
      return fetchAPI<{ success: boolean; deliverables: any[]; total: number; stale_count: number; stale_deliverables: any[] }>(
        `/api/deliverables/dashboard${qs ? `?${qs}` : ''}`, z.any());
    },

    // File attachments
    files: {
      list: (deliverableId: string) =>
        fetchAPI<{ success: boolean; files: any[] }>(
          `/api/deliverables/${deliverableId}/files`, z.any()),

      upload: async (deliverableId: string, file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        const token = await getAuthToken();
        const response = await fetch(`${API_BASE_URL}/api/deliverables/${deliverableId}/files`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({ detail: 'Upload failed' }));
          throw new Error(err.detail || 'Upload failed');
        }
        return response.json();
      },

      downloadFile: async (deliverableId: string, fileId: string, fileName: string) => {
        const token = await getAuthToken();
        const response = await fetch(
          `${API_BASE_URL}/api/deliverables/${deliverableId}/files/${fileId}/download`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error('Download failed');
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
      },

      delete: async (deliverableId: string, fileId: string) => {
        const token = await getAuthToken();
        const response = await fetch(`${API_BASE_URL}/api/deliverables/${deliverableId}/files/${fileId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (!response.ok) throw new Error('Delete failed');
        return response.json();
      },

      listVersions: (deliverableId: string, fileId: string) =>
        fetchAPI<{ success: boolean; versions: any[]; total: number; file_id: string }>(
          `/api/deliverables/${deliverableId}/files/${fileId}/versions`, z.any()),

      uploadVersion: async (deliverableId: string, fileId: string, file: File, changeNote?: string) => {
        const formData = new FormData();
        formData.append('file', file);
        if (changeNote) {
          formData.append('change_note', changeNote);
        }
        const token = await getAuthToken();
        const response = await fetch(
          `${API_BASE_URL}/api/deliverables/${deliverableId}/files/${fileId}/versions`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
            body: formData,
          }
        );
        if (!response.ok) {
          const err = await response.json().catch(() => ({ detail: 'Upload failed' }));
          throw new Error(err.detail || 'Upload failed');
        }
        return response.json();
      },

      downloadVersion: async (deliverableId: string, fileId: string, versionId: string, fileName: string) => {
        const token = await getAuthToken();
        const response = await fetch(
          `${API_BASE_URL}/api/deliverables/${deliverableId}/files/versions/${versionId}/download`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error('Download failed');
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
      },
    },

    // Linked documents (from company document library)
    documents: {
      list: (deliverableId: string) =>
        fetchAPI<{ success: boolean; documents: any[]; count: number }>(
          `/api/deliverables/${deliverableId}/documents`, z.any()),

      link: (deliverableId: string, documentId: string) =>
        fetchAPI<{ success: boolean; link: any }>(
          `/api/deliverables/${deliverableId}/documents`, z.any(), {
            method: 'POST',
            body: JSON.stringify({ document_id: documentId }),
          }),

      unlink: async (deliverableId: string, documentId: string) => {
        const token = await getAuthToken();
        const response = await fetch(
          `${API_BASE_URL}/api/deliverables/${deliverableId}/documents/${documentId}`,
          { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error('Unlink failed');
        return response.json();
      },
    },
  },

  // Submissions (grouping layer for deliverables)
  submissions: {
    list: (projectId: string) =>
      fetchAPI<{ success: boolean; submissions: any[]; total: number; project_id: string }>(
        `/api/projects/${projectId}/submissions`, z.any()),

    get: (submissionId: string) =>
      fetchAPI<{ success: boolean; submission: any }>(
        `/api/submissions/${submissionId}`, z.any()),

    create: (projectId: string, data: { name: string; type?: string; due_date?: string; sort_order?: number }) =>
      fetchAPI<{ success: boolean; submission: any }>(
        `/api/projects/${projectId}/submissions`, z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (submissionId: string, data: { name?: string; type?: string; due_date?: string; sort_order?: number }) =>
      fetchAPI<{ success: boolean; submission: any }>(
        `/api/submissions/${submissionId}`, z.any(), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (submissionId: string) =>
      fetchAPI<{ success: boolean }>(
        `/api/submissions/${submissionId}`, z.any(), {
        method: 'DELETE',
      }),
  },

  // Construction Submittals
  submittals: {
    list: (projectId: string) =>
      fetchAPI<{ success: boolean; submittals: any[]; total: number; project_id: string }>(
        `/api/projects/${projectId}/submittals`, z.any()),

    get: (submittalId: string) =>
      fetchAPI<{ success: boolean; submittal: any; items: any[]; files: any[]; revisions: any[] }>(
        `/api/submittals/${submittalId}`, z.any()),

    create: (projectId: string, data: { title: string; description?: string; spec_section?: string; submitted_to?: string; submitted_to_email?: string; submitted_to_org_id?: string; date_required?: string }) =>
      fetchAPI<{ success: boolean; submittal: any }>(
        `/api/projects/${projectId}/submittals`, z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (submittalId: string, data: Record<string, any>) =>
      fetchAPI<{ success: boolean; submittal: any }>(
        `/api/submittals/${submittalId}`, z.any(), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (submittalId: string) =>
      fetchAPI<{ success: boolean }>(
        `/api/submittals/${submittalId}`, z.any(), {
        method: 'DELETE',
      }),

    updateStatus: (submittalId: string, data: { status: string; reviewer_comments?: string; date_returned?: string }) =>
      fetchAPI<{ success: boolean; submittal: any }>(
        `/api/submittals/${submittalId}/status`, z.any(), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    // Items
    createItem: (submittalId: string, data: { description: string; manufacturer?: string; model_number?: string }) =>
      fetchAPI<{ success: boolean; item: any }>(
        `/api/submittals/${submittalId}/items`, z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateItem: (submittalId: string, itemId: string, data: { description?: string; manufacturer?: string; model_number?: string }) =>
      fetchAPI<{ success: boolean; item: any }>(
        `/api/submittals/${submittalId}/items/${itemId}`, z.any(), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    deleteItem: (submittalId: string, itemId: string) =>
      fetchAPI<{ success: boolean }>(
        `/api/submittals/${submittalId}/items/${itemId}`, z.any(), {
        method: 'DELETE',
      }),

    // Files
    files: {
      list: (submittalId: string) =>
        fetchAPI<{ files: any[]; count: number }>(
          `/api/submittals/${submittalId}/files`, z.any()),

      upload: async (submittalId: string, file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        const token = await getAuthToken();
        const response = await fetch(`${API_BASE_URL}/api/submittals/${submittalId}/files`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({ detail: 'Upload failed' }));
          throw new Error(err.detail || 'Upload failed');
        }
        return response.json();
      },

      downloadFile: async (submittalId: string, fileId: string, fileName: string) => {
        const token = await getAuthToken();
        const response = await fetch(
          `${API_BASE_URL}/api/submittals/${submittalId}/files/${fileId}/download`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error('Download failed');
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
      },

      delete: async (submittalId: string, fileId: string) => {
        const token = await getAuthToken();
        const response = await fetch(`${API_BASE_URL}/api/submittals/${submittalId}/files/${fileId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (!response.ok) throw new Error('Delete failed');
        return response.json();
      },
    },

    // Cover sheet
    downloadCoverSheet: async (submittalId: string, submittalNumber: string) => {
      const token = await getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}/api/submittals/${submittalId}/cover-sheet/download`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${submittalNumber}-CoverSheet.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    },

    // Email
    sendEmail: (submittalId: string, data: { recipient: string; custom_note?: string; reply_to_override?: string }) =>
      fetchAPI<{ success: boolean; sent_at: string; sent_to: string; message_id: string }>(
        `/api/submittals/${submittalId}/send-email`, z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    // Revisions
    listRevisions: (submittalId: string) =>
      fetchAPI<{ success: boolean; revisions: any[]; total: number }>(
        `/api/submittals/${submittalId}/revisions`, z.any()),

    createRevision: (submittalId: string, data?: { reviewer_comments?: string }) =>
      fetchAPI<{ success: boolean; revision: any }>(
        `/api/submittals/${submittalId}/revisions`, z.any(), {
        method: 'POST',
        body: JSON.stringify(data || {}),
      }),

    // Log export
    exportLog: async (projectId: string, format: 'csv' | 'pdf' = 'csv') => {
      const token = await getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}/api/projects/${projectId}/submittal-log/export?format=${format}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `submittal-log.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    },

    // Annotations (R1: PDF Annotation Overlay)
    annotations: {
      list: (submittalId: string, fileId: string) =>
        fetchAPI<{ success: boolean; annotations: any[]; total: number }>(
          `/api/submittals/${submittalId}/annotations?file_id=${encodeURIComponent(fileId)}`,
          z.any()
        ),

      create: (submittalId: string, data: {
        file_id: string;
        page_number: number;
        annotation_type: 'text' | 'stamp' | 'freehand';
        data: Record<string, any>;
      }) =>
        fetchAPI<{ success: boolean; annotation: any }>(
          `/api/submittals/${submittalId}/annotations`, z.any(), {
          method: 'POST',
          body: JSON.stringify(data),
        }),

      delete: (submittalId: string, annotationId: string) =>
        fetchAPI<{ success: boolean }>(
          `/api/submittals/${submittalId}/annotations/${annotationId}`, z.any(), {
          method: 'DELETE',
        }),

      exportFlatPdf: async (submittalId: string, fileId: string, fileName: string) => {
        const token = await getAuthToken();
        const response = await fetch(
          `${API_BASE_URL}/api/submittals/${submittalId}/export-flat-pdf`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ file_id: fileId }),
          }
        );
        if (!response.ok) throw new Error('Export failed');
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const base = fileName.toLowerCase().endsWith('.pdf')
          ? fileName.slice(0, -4) + '-annotated.pdf'
          : fileName + '-annotated.pdf';
        a.download = base;
        a.click();
        URL.revokeObjectURL(url);
      },

      sendForReview: (submittalId: string, data: {
        file_id: string;
        engineer_contact_ids: string[];
        note?: string;
      }) =>
        fetchAPI<{ success: boolean; sent_to: string[]; errors: string[]; sent_at: string }>(
          `/api/submittals/${submittalId}/send-for-review`, z.any(), {
          method: 'POST',
          body: JSON.stringify(data),
        }),
    },
  },

  // Document Reviews (Phase 13 — V164 substrate)
  documentReviews: {
    list: (params?: { status?: string }) => {
      const sp = new URLSearchParams();
      if (params?.status) sp.set('status', params.status);
      const qs = sp.toString() ? `?${sp.toString()}` : '';
      return fetchAPI<{ success: boolean; reviews: any[]; total: number }>(
        `/api/document-reviews${qs}`, z.any());
    },

    get: (reviewId: string) =>
      fetchAPI<{ success: boolean; review: any }>(
        `/api/document-reviews/${reviewId}`, z.any()),

    create: (data: {
      file_ref: string;
      mode: 'comment' | 'approve' | 'sign';
      submittal_id?: string;
      proposal_id?: string;
      client_action_id?: string;
      file_revision?: number;
    }) =>
      fetchAPI<{ success: boolean; review: any }>(
        `/api/document-reviews`, z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    forSubmittal: (submittalId: string, fileRef: string) =>
      fetchAPI<{ success: boolean; review: any }>(
        `/api/document-reviews/for-submittal/${submittalId}?file_ref=${encodeURIComponent(fileRef)}`,
        z.any()),

    updateStatus: (reviewId: string, data: { status: string; outcome?: string }) =>
      fetchAPI<{ success: boolean; review: any }>(
        `/api/document-reviews/${reviewId}/status`, z.any(), {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    cancel: (reviewId: string) =>
      fetchAPI<{ success: boolean }>(
        `/api/document-reviews/${reviewId}/cancel`, z.any(), {
        method: 'POST',
      }),

    // F10: per-review audit trail (event log). Used by the admin review
    // detail panel to show who-did-what-when on this review.
    auditEvents: (reviewId: string) =>
      fetchAPI<{
        success: boolean;
        events: Array<{
          id: string;
          event: string;
          actor_id?: string | null;
          ip?: string | null;
          user_agent?: string | null;
          details?: Record<string, any> | null;
          created_at: string;
        }>;
        total: number;
      }>(`/api/document-reviews/${reviewId}/audit`, z.any()),

    // D-Q5: draft save — no email, just persist recipient selection
    saveDraft: (reviewId: string, data: { recipient_emails: string[]; note?: string }) =>
      fetchAPI<{ success: boolean; review: any }>(
        `/api/document-reviews/${reviewId}/save-draft`, z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    // D-Q5: send — transitions draft → active, dispatches Mailgun emails
    send: (reviewId: string, data: { recipient_emails: string[]; note?: string }) =>
      fetchAPI<{ success: boolean; sent_to: string[]; errors: string[] }>(
        `/api/document-reviews/${reviewId}/send`, z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    annotations: {
      list: (reviewId: string) =>
        fetchAPI<{ success: boolean; annotations: any[]; total: number }>(
          `/api/document-reviews/${reviewId}/annotations`, z.any()),

      create: (reviewId: string, data: {
        page_number: number;
        type: 'text' | 'stamp' | 'freehand' | 'signature';
        coords?: Record<string, any>;
        data: Record<string, any>;
      }) =>
        fetchAPI<{ success: boolean; annotation: any }>(
          `/api/document-reviews/${reviewId}/annotations`, z.any(), {
          method: 'POST',
          body: JSON.stringify(data),
        }),

      delete: (reviewId: string, annotationId: string) =>
        fetchAPI<{ success: boolean }>(
          `/api/document-reviews/${reviewId}/annotations/${annotationId}`, z.any(), {
          method: 'DELETE',
        }),

      exportFlatPdf: async (reviewId: string) => {
        const token = await getAuthToken();
        const response = await fetch(
          `${API_BASE_URL}/api/document-reviews/${reviewId}/export-flat-pdf`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) throw new Error('Export failed');
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `document-review-${reviewId}-annotated.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      },
    },

    // Upload externally-stamped PDF with XHR for upload progress events
    uploadExternallyStamped: async (
      reviewId: string,
      file: File,
      onProgress?: (pct: number) => void
    ): Promise<{ success: boolean; inspector_result: ExternalStampInspectorResult }> => {
      const token = await getAuthToken();
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const formData = new FormData();
        formData.append('file', file);

        if (onProgress) {
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              onProgress(Math.round((e.loaded / e.total) * 100));
            }
          });
        }

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText)); }
            catch { reject(new Error('Invalid response from server')); }
          } else if (xhr.status === 401 || xhr.status === 403) {
            reject(new Error("You don't have permission to upload stamps to this review"));
          } else {
            try {
              const errBody = JSON.parse(xhr.responseText);
              reject(makeUploadHTTPError(errBody.detail || `Upload failed (${xhr.status})`, xhr.status));
            } catch {
              reject(makeUploadHTTPError(`Upload failed (${xhr.status})`, xhr.status));
            }
          }
        });

        xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
        xhr.addEventListener('abort', () => reject(new Error('Upload cancelled')));

        xhr.open('POST', `${API_BASE_URL}/api/document-reviews/${reviewId}/upload-externally-stamped`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(formData);
      });
    },

    confirmExternalStamp: (reviewId: string) =>
      fetchAPI<{ success: boolean; status: string; audit_cert_pdf_ref?: string }>(
        `/api/document-reviews/${reviewId}/confirm-external-stamp`,
        z.object({
          success: z.boolean(),
          status: z.string(),
          audit_cert_pdf_ref: z.string().optional(),
        }),
        { method: 'POST' }
      ),

    downloadAuditCert: async (reviewId: string) => {
      const token = await getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}/api/document-reviews/${reviewId}/download-audit-cert`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-cert-${reviewId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },

    /**
     * Download the current PDF bytes for a document review.
     *
     * Backend (`/api/document-reviews/{id}/download`) prefers the
     * externally-stamped variant when present and falls back to the original
     * file_ref. Phase 4 R4: appends a cache-bust param so re-opening a review
     * post-confirm+lock guarantees a fresh fetch (the PAdES widget lives in
     * those bytes, not in the UI).
     */
    downloadOriginalPdf: async (reviewId: string): Promise<string> => {
      const token = await getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}/api/document-reviews/${reviewId}/download?t=${Date.now()}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          cache: 'no-store',
        }
      );
      if (!response.ok) throw new Error('Failed to load review file');
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    },
  },

  // Site Visits (Phase 10d.1 - Field Review Module)
  siteVisits: {
    list: (projectId: string, params?: { start_date?: string; end_date?: string; visit_type?: string; status?: string }) => {
      const sp = new URLSearchParams();
      if (params?.start_date) sp.set('start_date', params.start_date);
      if (params?.end_date) sp.set('end_date', params.end_date);
      if (params?.visit_type) sp.set('visit_type', params.visit_type);
      if (params?.status) sp.set('status', params.status);
      const qs = sp.toString() ? `?${sp.toString()}` : '';
      return fetchAPI<{ success: boolean; site_visits: any[]; total: number; project_id: string }>(
        `/api/projects/${projectId}/site-visits${qs}`, z.any());
    },
    get: (visitId: string) =>
      fetchAPI<{ success: boolean; site_visit: any; photos: any[]; checklist_items: any[] }>(
        `/api/site-visits/${visitId}`, z.any()),
    create: (projectId: string, data: Record<string, any>) =>
      fetchAPI<{ success: boolean; site_visit: any }>(
        `/api/projects/${projectId}/site-visits`, z.any(), {
          method: 'POST', body: JSON.stringify(data),
        }),
    update: (visitId: string, data: Record<string, any>) =>
      fetchAPI<{ success: boolean; site_visit: any }>(
        `/api/site-visits/${visitId}`, z.any(), {
          method: 'PATCH', body: JSON.stringify(data),
        }),
    delete: (visitId: string) =>
      fetchAPI<{ success: boolean }>(
        `/api/site-visits/${visitId}`, z.any(), { method: 'DELETE' }),
    complete: (visitId: string) =>
      fetchAPI<{ success: boolean; site_visit: any }>(
        `/api/site-visits/${visitId}/complete`, z.any(), { method: 'POST', body: '{}' }),
    downloadReport: async (visitId: string) => {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/site-visits/${visitId}/report/download`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to download report');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Use filename from Content-Disposition header ("Project Name - Report Title.pdf")
      const disposition = response.headers.get('Content-Disposition') ?? '';
      const match = disposition.match(/filename="([^"]+)"/);
      a.download = match?.[1] ?? `site-visit-report-${visitId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    },
    // R1: Fetch current weather for a project's address (correct backend route)
    getWeather: (projectId: string) =>
      fetchAPI<{ success: boolean; weather: any; address: string }>(
        `/api/site-visits/weather?project_id=${projectId}`, z.any()),
    // R4: Fetch PDF bytes as a blob URL for inline preview (caller must revoke)
    previewReportBlob: async (visitId: string): Promise<string> => {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/site-visits/${visitId}/report/download`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to load report preview');
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    },
    exportLog: async (projectId: string, params?: { start_date?: string; end_date?: string }) => {
      const token = await getAuthToken();
      const sp = new URLSearchParams();
      if (params?.start_date) sp.set('start_date', params.start_date);
      if (params?.end_date) sp.set('end_date', params.end_date);
      const qs = sp.toString() ? `?${sp.toString()}` : '';
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/site-visit-log/export${qs}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'site-visit-log.csv';
      a.click();
      URL.revokeObjectURL(url);
    },
    photos: {
      list: (visitId: string) =>
        fetchAPI<{ photos: any[]; count: number }>(`/api/site-visits/${visitId}/photos`, z.any()),
      upload: async (visitId: string, file: File) => {
        const token = await getAuthToken();
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetch(`${API_BASE_URL}/api/site-visits/${visitId}/photos`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.detail || 'Upload failed');
        }
        return response.json();
      },
      downloadUrl: (visitId: string, photoId: string) =>
        `${API_BASE_URL}/api/site-visits/${visitId}/photos/${photoId}/download`,
      fetchBlob: async (visitId: string, photoId: string): Promise<string> => {
        const token = await getAuthToken();
        const response = await fetch(
          `${API_BASE_URL}/api/site-visits/${visitId}/photos/${photoId}/download`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error(`Failed to load photo: ${response.status}`);
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.startsWith('image/')) {
          throw new Error(`Unexpected content type: ${contentType}`);
        }
        const blob = await response.blob();
        return URL.createObjectURL(blob);
      },
      update: (visitId: string, photoId: string, data: { caption?: string; tags?: string[]; sort_order?: number }) =>
        fetchAPI<{ success: boolean; photo: any }>(
          `/api/site-visits/${visitId}/photos/${photoId}`, z.any(), {
            method: 'PATCH', body: JSON.stringify(data),
          }),
      delete: async (visitId: string, photoId: string) => {
        const token = await getAuthToken();
        const response = await fetch(
          `${API_BASE_URL}/api/site-visits/${visitId}/photos/${photoId}`,
          { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error('Delete failed');
        return response.json();
      },
    },
    checklist: {
      create: (visitId: string, data: { description: string; status?: string; notes?: string; photo_id?: string }) =>
        fetchAPI<{ success: boolean; item: any }>(
          `/api/site-visits/${visitId}/checklist-items`, z.any(), {
            method: 'POST', body: JSON.stringify(data),
          }),
      update: (visitId: string, itemId: string, data: Record<string, any>) =>
        fetchAPI<{ success: boolean; item: any }>(
          `/api/site-visits/${visitId}/checklist-items/${itemId}`, z.any(), {
            method: 'PATCH', body: JSON.stringify(data),
          }),
      delete: (visitId: string, itemId: string) =>
        fetchAPI<{ success: boolean }>(
          `/api/site-visits/${visitId}/checklist-items/${itemId}`, z.any(), {
            method: 'DELETE',
          }),
    },
    getWeather: (projectId: string) =>
      fetchAPI<{ success: boolean; weather: any; address: string }>(
        `/api/site-visits/weather?project_id=${encodeURIComponent(projectId)}`, z.any()),
    // R2: post-create editable General Conditions + Weather
    updateConditions: (visitId: string, data: { general_conditions?: string; weather_notes?: string }) =>
      fetchAPI<{ success: boolean; site_visit: any }>(
        `/api/site-visits/${visitId}/conditions`, z.any(), {
          method: 'PATCH', body: JSON.stringify(data),
        }),
    reopen: (visitId: string, reason: string) =>
      fetchAPI<{ success: boolean; site_visit: any }>(
        `/api/site-visits/${visitId}/reopen`, z.any(), {
          method: 'POST', body: JSON.stringify({ reason }),
        }),
    // R4: PDF Preview & Send
    emailRecipients: (visitId: string) =>
      fetchAPI<{ success: boolean; recipients: Array<{ email: string; name: string; source: string }> }>(
        `/api/site-visits/${visitId}/email-recipients`, z.any()),
    sendEmail: (
      visitId: string,
      data: { to_recipients: string[]; cc_recipients?: string[]; custom_note?: string }
    ) =>
      fetchAPI<{ success: boolean; pdf_sha256: string; message_id: string; sent_to: string[] }>(
        `/api/site-visits/${visitId}/send-email`, z.any(), {
          method: 'POST', body: JSON.stringify(data),
        }),
    submitWithoutSending: (visitId: string) =>
      fetchAPI<{ success: boolean; site_visit: any }>(
        `/api/site-visits/${visitId}/submit-without-sending`, z.any(), {
          method: 'POST', body: JSON.stringify({}),
        }),
  },

  // Document Management (File Management Feature)
  documents: {
    list: (params?: { folder_id?: string; document_type?: string; search?: string; project_id?: string }) => {
      const searchParams = new URLSearchParams();
      if (params?.folder_id) searchParams.set('folder_id', params.folder_id);
      if (params?.document_type) searchParams.set('document_type', params.document_type);
      if (params?.search) searchParams.set('search', params.search);
      if (params?.project_id !== undefined) searchParams.set('project_id', params.project_id);
      const qs = searchParams.toString();
      return fetchAPI<{ success: boolean; documents: any[]; total: number }>(
        `/api/documents${qs ? `?${qs}` : ''}`, z.any());
    },

    get: (id: string) =>
      fetchAPI<{ success: boolean; document: any; versions: any[] }>(
        `/api/documents/${id}`, z.any()),

    createLink: (data: { title: string; description?: string; folder_id?: string; project_id?: string; external_url: string }) =>
      fetchAPI<{ success: boolean; document: any }>(
        `/api/documents`, z.any(), {
        method: 'POST',
        body: JSON.stringify({ ...data, document_type: 'link' }),
      }),

    upload: async (file: File, title: string, description?: string, folderId?: string, projectId?: string) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title);
      if (description) formData.append('description', description);
      if (folderId) formData.append('folder_id', folderId);
      if (projectId) formData.append('project_id', projectId);
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/documents/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ detail: 'Upload failed' }));
        throw new Error(err.detail || 'Upload failed');
      }
      return response.json();
    },

    update: (id: string, data: { title?: string; description?: string; folder_id?: string; external_url?: string }) =>
      fetchAPI<{ success: boolean; document: any }>(
        `/api/documents/${id}`, z.any(), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: async (id: string) => {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/documents/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Delete failed');
      return response.json();
    },

    download: async (id: string, fileName: string) => {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/documents/${id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    },

    uploadVersion: async (documentId: string, file: File, changeNote?: string) => {
      const formData = new FormData();
      formData.append('file', file);
      if (changeNote) formData.append('change_note', changeNote);
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/documents/${documentId}/versions`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ detail: 'Upload failed' }));
        throw new Error(err.detail || 'Upload failed');
      }
      return response.json();
    },

    listVersions: (documentId: string) =>
      fetchAPI<{ success: boolean; versions: any[]; total: number }>(
        `/api/documents/${documentId}/versions`, z.any()),

    downloadVersion: async (versionId: string, fileName: string) => {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/documents/versions/${versionId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    },

    // Linked deliverables (reverse lookup)
    linkedDeliverables: (documentId: string) =>
      fetchAPI<{ success: boolean; deliverables: any[]; count: number }>(
        `/api/documents/${documentId}/deliverables`, z.any()),

    // Folder management
    folders: {
      list: () =>
        fetchAPI<{ success: boolean; folders: any[]; unfiled_count: number; total: number }>(
          `/api/documents/folders`, z.any()),

      create: (data: { name: string; description?: string; sort_order?: number }) =>
        fetchAPI<{ success: boolean; folder: any }>(
          `/api/documents/folders`, z.any(), {
          method: 'POST',
          body: JSON.stringify(data),
        }),

      update: (id: string, data: { name?: string; description?: string; sort_order?: number }) =>
        fetchAPI<{ success: boolean; folder: any }>(
          `/api/documents/folders/${id}`, z.any(), {
          method: 'PUT',
          body: JSON.stringify(data),
        }),

      delete: async (id: string) => {
        const token = await getAuthToken();
        const response = await fetch(`${API_BASE_URL}/api/documents/folders/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (!response.ok) throw new Error('Delete failed');
        return response.json();
      },
    },
  },

  // Webhooks / Integrations (Phase 10c.7 - Teams Integration)
  webhooks: {
    listEndpoints: (includeInactive = false) =>
      fetchAPI<{ success: boolean; endpoints: any[]; total: number }>(
        `/api/webhook-endpoints?include_inactive=${includeInactive}`, z.any()),

    getEndpoint: (id: string) =>
      fetchAPI<{ success: boolean; endpoint: any }>(
        `/api/webhook-endpoints/${id}`, z.any()),

    createEndpoint: (data: { name: string; url: string; event_types: string[] }) =>
      fetchAPI<{ success: boolean; endpoint: any; signing_secret: string; message: string }>(
        `/api/webhook-endpoints`, z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateEndpoint: (id: string, data: Record<string, any>) =>
      fetchAPI<{ success: boolean; endpoint: any }>(
        `/api/webhook-endpoints/${id}`, z.any(), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    deleteEndpoint: (id: string) =>
      fetchAPI<{ success: boolean; message: string }>(
        `/api/webhook-endpoints/${id}`, z.any(), {
        method: 'DELETE',
      }),

    testEndpoint: (id: string) =>
      fetchAPI<{ success: boolean; message: string }>(
        `/api/webhook-endpoints/${id}/test`, z.any(), {
        method: 'POST',
      }),

    listEvents: (endpointId?: string, limit = 50) =>
      fetchAPI<{ success: boolean; events: any[]; total: number }>(
        `/api/webhook-events?${endpointId ? `endpoint_id=${endpointId}&` : ''}limit=${limit}`, z.any()),

    eventStats: () =>
      fetchAPI<{ success: boolean; stats: { success: number; failed: number; pending: number; total: number } }>(
        `/api/webhook-events/stats`, z.any()),

    // Slack Bot (Phase 10c.10)
    validateSlackToken: (botToken: string) =>
      fetchAPI<{ success: boolean; valid: boolean; bot_info?: any; channels?: any[]; error?: string }>(
        `/api/slack-bot/validate-token?bot_token=${encodeURIComponent(botToken)}`, z.any(), {
        method: 'POST',
      }),

    setupSlackBot: (data: { name: string; bot_token: string; channel_id: string; event_types: string[]; channel_mode: string }) =>
      fetchAPI<{ success: boolean; endpoint: any; slack_info: any; message: string }>(
        `/api/slack-bot/setup`, z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  // Dropbox integration
  dropbox: {
    getStatus: () =>
      fetchAPI<{ connected: boolean; display_name?: string; email?: string; target_folder?: string; last_sync_at?: string; last_sync_error?: string }>(
        `/api/integrations/dropbox/status`, z.any()),

    connect: () =>
      fetchAPI<{ auth_url: string; state: string }>(
        `/api/integrations/dropbox/connect`, z.any()),

    updateFolder: (data: { target_folder: string }) =>
      fetchAPI<{ connected: boolean; target_folder?: string }>(
        `/api/integrations/dropbox/settings`, z.any(), {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    disconnect: () =>
      fetchAPI<{ status: string }>(
        `/api/integrations/dropbox/disconnect`, z.any(), {
        method: 'DELETE',
      }),

    syncAll: () =>
      fetchAPI<{ queued: number; skipped: number }>(
        `/api/integrations/dropbox/sync-all`, z.any(), {
        method: 'POST',
      }),
  },

  // QuickBooks Online integration
  quickbooks: {
    getStatus: () =>
      fetchAPI<{ success: boolean; is_connected: boolean; realm_id?: string; company_name?: string; connected_by?: string; connected_at?: string; last_refreshed_at?: string }>(
        `/api/quickbooks/status`, z.any()),

    authorize: () =>
      fetchAPI<{ success: boolean; auth_url: string; state: string }>(
        `/api/quickbooks/authorize`, z.any()),

    callback: (data: { code: string; state: string; realm_id: string }) =>
      fetchAPI<{ success: boolean; message: string; realm_id: string; company_name?: string }>(
        `/api/quickbooks/callback`, z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    disconnect: () =>
      fetchAPI<{ success: boolean; message: string }>(
        `/api/quickbooks/disconnect`, z.any(), {
        method: 'POST',
      }),

    syncInvoice: (invoiceId: string) =>
      fetchAPI<{ success: boolean; message: string; qbo_invoice_id?: string; qbo_customer_id?: string; action?: string; total_verified?: boolean; error?: string }>(
        `/api/quickbooks/sync/invoice/${invoiceId}`, z.any(), {
        method: 'POST',
      }),

    getSyncStatus: (invoiceId: string) =>
      fetchAPI<{ success: boolean; is_synced: boolean; sync_status: string; qbo_id?: string; last_synced_at?: string; sync_error?: string }>(
        `/api/quickbooks/sync/status/${invoiceId}`, z.any()),
  },

  // Phase 10b.7 - Team roster for user linking
  team: {
    members: () =>
      fetchAPI<{ members: any[]; count: number }>('/api/team/members', z.any()),
  },

  // External Views - token-based public access
  externalViews: {
    // Public (no auth required)
    getPublic: async (token: string, password?: string) => {
      const headers: Record<string, string> = {};
      if (password) headers['X-View-Password'] = password;
      const response = await fetch(`${API_BASE_URL}/api/ext/${token}`, { headers });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error: any = new Error(errorData.detail || 'Failed to load view');
        error.status = response.status;
        error.data = errorData;
        throw error;
      }
      return response.json();
    },

    // Public meta endpoint — returns resolved theme for CSS injection (no auth required)
    getPublicMeta: async (token: string): Promise<{ view: any; theme: any } | null> => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/ext/${token}/meta`);
        if (!response.ok) return null;
        return response.json();
      } catch {
        return null;
      }
    },

    // Invitation meta endpoint — returns tenant theme for invitation acceptance page (no auth required)
    getInvitationMeta: async (token: string): Promise<{ view: any; theme: any } | null> => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/ext/invitation/${encodeURIComponent(token)}/meta`);
        if (!response.ok) return null;
        return response.json();
      } catch {
        return null;
      }
    },

    // Public theme endpoint — fetches tenant theme by slug for portal login page (no auth required)
    getPublicTheme: async (tenantSlug: string): Promise<any | null> => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/themes/public/${encodeURIComponent(tenantSlug)}`);
        if (!response.ok) return null;
        return response.json();
      } catch {
        return null;
      }
    },

    // Authenticated (management)
    list: () =>
      fetchAPI<{ success: boolean; views: any[]; total: number }>(
        '/api/external-views', z.any()),

    get: (id: string) =>
      fetchAPI<{ success: boolean; view: any; access_log: any[] }>(
        `/api/external-views/${id}`, z.any()),

    create: (data: { name: string; panels: any[]; branding?: any; password?: string; expires_at?: string }) =>
      fetchAPI<{ success: boolean; view: any }>(
        '/api/external-views', z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: any) =>
      fetchAPI<{ success: boolean; view: any }>(
        `/api/external-views/${id}`, z.any(), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      fetchAPI<{ success: boolean; message: string }>(
        `/api/external-views/${id}`, z.any(), {
        method: 'DELETE',
      }),

    // EXTERNAL role: list views assigned to the authenticated user
    myViews: () =>
      fetchAPI<{ success: boolean; views: any[]; total: number }>(
        '/api/external-views/my-views', z.any()),

    // Phase 10d.4: Accept an invitation (authenticated). Returns view_token for redirect.
    acceptInvitation: (invitationToken: string) =>
      fetchAPI<{ success: boolean; membership: any; view_token: string | null }>(
        '/api/external-views/members/accept-invitation', z.any(), {
        method: 'POST',
        body: JSON.stringify({ invitation_token: invitationToken }),
      }),

    // Member management (internal users only)
    addMember: (viewId: string, data: { email: string; view_id: string }) =>
      fetchAPI<{ success: boolean; membership: any }>(
        `/api/external-views/${viewId}/members`, z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    removeMember: (viewId: string, memberEmail: string) =>
      fetchAPI<{ success: boolean; message: string }>(
        `/api/external-views/${viewId}/members/${encodeURIComponent(memberEmail)}`, z.any(), {
        method: 'DELETE',
      }),

    listMembers: (viewId: string) =>
      fetchAPI<{ success: boolean; members: any[]; total: number }>(
        `/api/external-views/${viewId}/members`, z.any()),

    // Phase 10d.3: Revoke a member (uses UUID, soft-delete)
    revokeMember: (viewId: string, memberId: string) =>
      fetchAPI<{ success: boolean; message: string }>(
        `/api/external-views/${viewId}/members/${memberId}`, z.any(), {
        method: 'DELETE',
      }),

    // Phase 10d.3: Re-invite a member (revoke then re-add)
    resendInvite: (viewId: string, data: { email: string }) =>
      fetchAPI<{ success: boolean; membership: any }>(
        `/api/external-views/${viewId}/members`, z.any(), {
        method: 'POST',
        body: JSON.stringify({ email: data.email, role: 'viewer' }),
      }),

    // R4: Public — list portal-visible documents for a token (no auth required)
    getPortalDocuments: async (token: string, docType?: string) => {
      const sp = new URLSearchParams();
      if (docType) sp.set('doc_type', docType);
      const qs = sp.toString() ? `?${sp.toString()}` : '';
      const response = await fetch(`${API_BASE_URL}/api/ext/${token}/documents${qs}`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error: any = new Error(errorData.detail || 'Failed to load documents');
        error.status = response.status;
        error.data = errorData;
        throw error;
      }
      return response.json() as Promise<{
        success: boolean;
        documents: Array<{
          id: string;
          title: string;
          status: string;
          scope_name: string | null;
          doc_type: string | null;
          files: Array<{ id: string; name: string; url: string; size?: number; uploaded_at?: string }>;
          updated_at: string;
        }>;
        total: number;
      }>;
    },

    // Public: submit an action to a panel (no auth required)
    postAction: async (token: string, payload: {
      action_type: string;
      panel_index: number;
      payload: Record<string, any>;
    }): Promise<{ success: boolean; result: any }> => {
      const response = await fetch(`${API_BASE_URL}/api/ext/${token}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error: any = new Error(errorData.detail || 'Action failed');
        error.status = response.status;
        error.data = errorData;
        throw error;
      }
      return response.json();
    },
  },

  // R3: Portal AI Summary settings
  portalAiSummary: {
    getSettings: () =>
      fetchAPI<{ success: boolean; enabled: boolean; email_enabled: boolean; custom_prompt: string | null }>(
        '/api/me/settings/portal-ai-summary', z.any()),

    updateSettings: (data: { enabled?: boolean; email_enabled?: boolean; custom_prompt?: string }) =>
      fetchAPI<{ success: boolean; message: string }>(
        '/api/me/settings/portal-ai-summary', z.any(), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    getLatest: () =>
      fetchAPI<{ success: boolean; summary: { summary_date: string; summary_text: string; email_sent: boolean; created_at: string; view_name: string } | null }>(
        '/api/me/settings/portal-ai-summary/latest', z.any()),
  },

  // Phase 10d.3: Project share convenience endpoint
  projectShare: {
    getForProject: (projectId: string) =>
      fetchAPI<{
        success: boolean;
        view: { id: string; name: string; token: string; welcome_message: string | null } | null;
        members: any[];
        share_url: string | null;
        portal_url: string | null;
      }>(`/api/projects/${projectId}/share`, z.any()),

    share: (projectId: string, data: {
      emails: string[];
      template?: 'simple' | 'standard' | 'enterprise';
      custom_panels?: string[];
      can_share?: boolean;
      welcome_message?: string;
    }) =>
      fetchAPI<{
        success: boolean;
        view: { id: string; name: string; token: string };
        share_url: string;
        members: any[];
        errors: Array<{ email: string; reason: string }>;
      }>(`/api/projects/${projectId}/share`, z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    listMembers: (viewId: string) =>
      fetchAPI<{ success: boolean; members: any[]; total: number }>(
        `/api/external-views/${viewId}/members`, z.any()),

    revokeMember: (viewId: string, memberId: string) =>
      fetchAPI<{ success: boolean; message: string }>(
        `/api/external-views/${viewId}/members/${memberId}`, z.any(), {
        method: 'DELETE',
      }),

    resendInvite: (viewId: string, data: { email: string }) =>
      fetchAPI<{ success: boolean; membership: any }>(
        `/api/external-views/${viewId}/members`, z.any(), {
        method: 'POST',
        body: JSON.stringify({ email: data.email, role: 'viewer' }),
      }),
  },

  // Document Reviews — portal context (D-Q8)
  // No-auth, token-gated endpoints used by external customers via PortalActionItemPage.
  // Mirrors the admin `api.documentReviews.annotations.*` surface for portal callers:
  //   - fetchReviewWithAnnotations: GET /api/portal/reviews/{id}?view_token=...
  //     (annotations are bundled in the review response — no separate list endpoint)
  //   - createAnnotation:          POST /api/portal/reviews/{id}/annotations?view_token=...
  // Headers: X-Actor-Session (required), X-View-Password (when view is password-protected).
  portalReviews: {
    fetchReviewWithAnnotations: async (
      reviewId: string,
      viewToken: string,
      actorId: string,
      password?: string,
    ): Promise<{ review: any; annotations: any[] }> => {
      const headers: Record<string, string> = { 'X-Actor-Session': actorId };
      if (password) headers['X-View-Password'] = password;
      const response = await fetch(
        `${API_BASE_URL}/api/portal/reviews/${reviewId}?view_token=${encodeURIComponent(viewToken)}`,
        { headers },
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error: any = new Error(errorData.detail || 'Failed to load portal review');
        error.status = response.status;
        throw error;
      }
      const data = await response.json();
      return {
        review: data.review,
        annotations: data.annotations || [],
      };
    },

    createAnnotation: async (
      reviewId: string,
      annData: {
        page_number: number;
        type: 'text' | 'stamp' | 'freehand' | 'highlight';
        coords?: Record<string, any>;
        data: Record<string, any>;
      },
      viewToken: string,
      actorId: string,
      password?: string,
    ): Promise<any> => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Actor-Session': actorId,
      };
      if (password) headers['X-View-Password'] = password;
      // Backend AnnotationCreatePortal requires `coords` (non-optional dict).
      // Mirror the admin path by defaulting to `{}` when caller doesn't supply it.
      const body = JSON.stringify({
        page_number: annData.page_number,
        type: annData.type,
        coords: annData.coords ?? {},
        data: annData.data,
      });
      const response = await fetch(
        `${API_BASE_URL}/api/portal/reviews/${reviewId}/annotations?view_token=${encodeURIComponent(viewToken)}`,
        { method: 'POST', headers, body },
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error: any = new Error(errorData.detail || 'Failed to save annotation');
        error.status = response.status;
        throw error;
      }
      return response.json();
    },

    // Phase 4 R3 — portal-side status transition. Used by the Confirm-Approval
    // dialog (in-portal customer flow). Same payload as the admin endpoint, but
    // routed through token + actor headers instead of Firebase auth.
    updateStatus: async (
      reviewId: string,
      data: { status: 'active' | 'completed'; outcome?: 'approved' },
      viewToken: string,
      actorId: string,
      password?: string,
    ): Promise<any> => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Actor-Session': actorId,
      };
      if (password) headers['X-View-Password'] = password;
      const response = await fetch(
        `${API_BASE_URL}/api/portal/reviews/${reviewId}/status?view_token=${encodeURIComponent(viewToken)}`,
        { method: 'PATCH', headers, body: JSON.stringify(data) },
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error: any = new Error(errorData.detail || 'Failed to update status');
        error.status = response.status;
        throw error;
      }
      return response.json();
    },

    // Download the PDF (sealed variant once R6 lands; original otherwise). Portal-side
    // mirror of the admin download endpoint — token-gated, no Firebase auth.
    downloadFile: async (
      reviewId: string,
      viewToken: string,
      actorId: string,
      filename: string,
      password?: string,
    ): Promise<void> => {
      const headers: Record<string, string> = { 'X-Actor-Session': actorId };
      if (password) headers['X-View-Password'] = password;
      const response = await fetch(
        `${API_BASE_URL}/api/portal/reviews/${reviewId}/file?view_token=${encodeURIComponent(viewToken)}`,
        { headers },
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error: any = new Error(errorData.detail || 'Failed to download file');
        error.status = response.status;
        throw error;
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'document.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },

  // planTEK Wave 1 — Portal Invoice Payments (EXTERNAL role users)
  portalPayments: {
    listInvoices: () =>
      fetchAPI<{
        success: boolean;
        invoices: Array<{
          id: string;
          invoice_number: string;
          total_amount: number;
          amount_paid: number;
          due_date: string | null;
          invoice_date: string | null;
          status: string;
          project_name: string | null;
          organization_name: string | null;
          /** The invoice version the client is shown; sent back with Pay (SPEC-invoice-merge-history R4). */
          version?: string | null;
        }>;
        total: number;
      }>('/api/portal/invoices', z.any()),

    getStripeConfig: () =>
      fetchAPI<{
        success: boolean;
        publishable_key: string;
        is_test_mode: boolean;
      }>('/api/portal/stripe-config', z.any()),

    // SPEC-invoice-merge-history R4 (R2 handoff): the version the client was SHOWN travels with the
    // request, so a merged survivor (whose obligation changed) refuses an old tab instead of
    // minting a charge for a stale amount.
    payInvoice: (invoiceId: string, expectedVersion?: string | null) =>
      fetchAPI<{
        success: boolean;
        client_secret: string;
        intent_id: string;
        amount_cents: number;
        invoice_number: string;
        timestamp: string;
      }>(`/api/portal/invoices/${invoiceId}/pay${
        expectedVersion ? `?expected_version=${encodeURIComponent(expectedVersion)}` : ''
      }`, z.any(), {
        method: 'POST',
        body: JSON.stringify({}),
      }),

    // V172 — invoice payments ledger (portal/client view)
    getPayments: (invoiceId: string) =>
      fetchAPI(`/api/portal/invoices/client/${invoiceId}/payments`, z.any()),
  },

  // Phase 10c.10 - Project members
  projectMembers: {
    list: (projectId: string) =>
      fetchAPI<{ success: boolean; members: any[]; total: number }>(
        `/api/projects/${projectId}/members`, z.any()),

    add: (projectId: string, data: { user_email: string; user_name?: string; role?: string }) =>
      fetchAPI<{ success: boolean; member: any }>(
        `/api/projects/${projectId}/members`, z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    remove: (projectId: string, userEmail: string) =>
      fetchAPI<{ success: boolean; message: string }>(
        `/api/projects/${projectId}/members/${encodeURIComponent(userEmail)}`, z.any(), {
        method: 'DELETE',
      }),
  },

  // planTEK — Client Action Items
  clientActions: {
    list: (projectId: string) =>
      fetchAPI<{ success: boolean; actions: any[]; total: number }>(
        `/api/projects/${projectId}/client-actions`, z.any()),

    create: (projectId: string, data: {
      title: string;
      action_type?: string;
      description?: string;
      due_date?: string;
      is_required?: boolean;
      position?: number;
    }) =>
      fetchAPI<{ success: boolean; action: any }>(
        `/api/projects/${projectId}/client-actions`, z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (projectId: string, actionId: string, data: {
      title?: string;
      action_type?: string;
      description?: string;
      due_date?: string;
      position?: number;
      is_required?: boolean;
    }) =>
      fetchAPI<{ success: boolean; action: any }>(
        `/api/projects/${projectId}/client-actions/${actionId}`, z.any(), {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (projectId: string, actionId: string) =>
      fetchAPI<{ success: boolean; message: string }>(
        `/api/projects/${projectId}/client-actions/${actionId}`, z.any(), {
        method: 'DELETE',
      }),

    // Public: mark action complete via portal token (no auth required)
    complete: async (token: string, actionId: string, completedByEmail: string) => {
      const response = await fetch(
        `${API_BASE_URL}/api/ext/${token}/actions/complete/${actionId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ completed_by_email: completedByEmail }),
        },
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error: any = new Error(errorData.detail || 'Failed to complete action');
        error.status = response.status;
        throw error;
      }
      return response.json();
    },
  },

  // RFI Module (Phase 10d.2)
  rfis: {
    list: (projectId: string, options: { status?: string; search?: string } = {}) => {
      const sp = new URLSearchParams();
      if (options.status) sp.set('status', options.status);
      if (options.search) sp.set('search', options.search);
      const qs = sp.toString() ? `?${sp.toString()}` : '';
      return fetchAPI<{ success: boolean; rfis: any[]; total: number; project_id: string }>(
        `/api/projects/${projectId}/rfis${qs}`, z.any());
    },

    get: (rfiId: string) =>
      fetchAPI<{ success: boolean; rfi: any }>(`/api/rfis/${rfiId}`, z.any()),

    create: (projectId: string, data: Record<string, any>) =>
      fetchAPI<{ success: boolean; rfi: any }>(
        `/api/projects/${projectId}/rfis`, z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (rfiId: string, data: Record<string, any>) =>
      fetchAPI<{ success: boolean; rfi: any }>(
        `/api/rfis/${rfiId}`, z.any(), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (rfiId: string) =>
      fetchAPI<{ success: boolean; message: string }>(
        `/api/rfis/${rfiId}`, z.any(), { method: 'DELETE' }),

    updateStatus: (rfiId: string, data: { status: string }) =>
      fetchAPI<{ success: boolean; rfi: any }>(
        `/api/rfis/${rfiId}/status`, z.any(), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    submitAnswer: (rfiId: string, data: Record<string, any>) =>
      fetchAPI<{ success: boolean; rfi: any }>(
        `/api/rfis/${rfiId}/answer`, z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    send: (rfiId: string, data: Record<string, any>) =>
      fetchAPI<{ success: boolean; rfi: any; sent_to: string; sent_at: string }>(
        `/api/rfis/${rfiId}/send`, z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    files: {
      list: (rfiId: string, fileType?: string) => {
        const qs = fileType ? `?file_type=${fileType}` : '';
        return fetchAPI<{ files: any[]; count: number }>(
          `/api/rfis/${rfiId}/files${qs}`, z.any());
      },

      upload: async (rfiId: string, file: File, fileType: 'question' | 'answer' = 'question') => {
        const token = await getAuthToken();
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetch(
          `${API_BASE_URL}/api/rfis/${rfiId}/files?file_type=${fileType}`,
          { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData },
        );
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.detail || 'Upload failed');
        }
        return response.json();
      },

      delete: async (rfiId: string, fileId: string) => {
        const token = await getAuthToken();
        const response = await fetch(
          `${API_BASE_URL}/api/rfis/${rfiId}/files/${fileId}`,
          { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } },
        );
        if (!response.ok) throw new Error('Delete failed');
        return response.json();
      },
    },
  },

  // ── Supplemental Instructions (R3) ─────────────────────────────────────────
  sis: {
    list: (projectId: string, options: { status?: string; search?: string } = {}) => {
      const qs = new URLSearchParams();
      if (options.status) qs.set('status', options.status);
      if (options.search) qs.set('search', options.search);
      const q = qs.toString();
      return fetchAPI<{ success: boolean; sis: any[]; total: number; project_id: string }>(
        `/api/projects/${projectId}/sis${q ? `?${q}` : ''}`, z.any());
    },
    get: (siId: string) =>
      fetchAPI<{ success: boolean; si: any }>(`/api/sis/${siId}`, z.any()),
    create: (projectId: string, data: Record<string, any>) =>
      fetchAPI<{ success: boolean; si: any }>(
        `/api/projects/${projectId}/sis`, z.any(), {
          method: 'POST', body: JSON.stringify(data),
        }),
    createFromRFI: (projectId: string, rfiId: string) =>
      fetchAPI<{ success: boolean; si: any; source_rfi: any }>(
        `/api/projects/${projectId}/sis/from-rfi/${rfiId}`, z.any(), {
          method: 'POST', body: JSON.stringify({}),
        }),
    update: (siId: string, data: Record<string, any>) =>
      fetchAPI<{ success: boolean; si: any }>(
        `/api/sis/${siId}`, z.any(), {
          method: 'PUT', body: JSON.stringify(data),
        }),
    delete: (siId: string) =>
      fetchAPI<{ success: boolean; message: string }>(
        `/api/sis/${siId}`, z.any(), { method: 'DELETE' }),
    updateStatus: (siId: string, data: { status: string }) =>
      fetchAPI<{ success: boolean; si: any }>(
        `/api/sis/${siId}/status`, z.any(), {
          method: 'PUT', body: JSON.stringify(data),
        }),
    distribute: (siId: string, data: Record<string, any>) =>
      fetchAPI<{ success: boolean; si: any; sent_to: string[]; failed: string[] }>(
        `/api/sis/${siId}/distribute`, z.any(), {
          method: 'POST', body: JSON.stringify(data),
        }),
    listForRFI: (rfiId: string) =>
      fetchAPI<{ success: boolean; sis: any[]; total: number; rfi_id: string }>(
        `/api/rfis/${rfiId}/sis`, z.any()),
    downloadPDF: async (siId: string, fileName: string) => {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/sis/${siId}/pdf/download`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to download SI PDF');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    },
  },

  // planTEK: communication panel — public (portal token) endpoints
  extMessages: {
    /** Send a message from the external portal (no auth, token-scoped). */
    send: async (token: string, projectId: string, content: string, senderEmail = '') => {
      const response = await fetch(
        `${API_BASE_URL}/api/ext/${token}/messages`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ project_id: projectId, content, sender_email: senderEmail }),
        },
      );
      if (!response.ok) {
        const err: any = await response.json().catch(() => ({}));
        const e: any = new Error(err.detail || 'Failed to send message');
        e.status = response.status;
        throw e;
      }
      return response.json() as Promise<{ success: boolean; message: any }>;
    },

    /** Poll for messages since a timestamp (or all if since omitted). */
    poll: async (token: string, projectId: string, since?: string) => {
      const sp = new URLSearchParams({ project_id: projectId });
      if (since) sp.set('since', since);
      const response = await fetch(
        `${API_BASE_URL}/api/ext/${token}/messages?${sp.toString()}`,
      );
      if (!response.ok) {
        const err: any = await response.json().catch(() => ({}));
        const e: any = new Error(err.detail || 'Failed to fetch messages');
        e.status = response.status;
        throw e;
      }
      return response.json() as Promise<{ success: boolean; messages: any[]; total: number }>;
    },
  },

  // planTEK: PM-client message thread (V107: project_messages)
  projectMessages: {
    // R2: Global unread summary across all projects
    unreadSummary: () =>
      fetchAPI<{ success: boolean; summary: any[]; total_unread: number }>(
        `/api/messages/unread-summary`, z.any()),

    list: (projectId: string) =>
      fetchAPI<{ success: boolean; messages: any[]; total: number }>(
        `/api/projects/${projectId}/messages`, z.any()),

    create: (projectId: string, data: { content: string; is_internal?: boolean }) =>
      fetchAPI<{ success: boolean; message: any }>(
        `/api/projects/${projectId}/messages`, z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    delete: (projectId: string, messageId: string) =>
      fetchAPI<{ success: boolean; message: string }>(
        `/api/projects/${projectId}/messages/${messageId}`, z.any(), {
        method: 'DELETE',
      }),

    markRead: (projectId: string, messageId: string) =>
      fetchAPI<{ success: boolean; message: any }>(
        `/api/projects/${projectId}/messages/${messageId}/read`, z.any(), {
        method: 'POST',
        body: '{}',
      }),
  },

  // Document Requirements Engine — R2/R3
  documentRequirements: {
    // ---- Catalog: types ----
    listTypes: () =>
      fetchAPI<{ requirement_types: any[] }>('/api/document-requirement-types', z.any()),

    createType: (data: {
      code: string;
      display_name?: string;
      description?: string;
      template_document_id?: string | null;
      default_recipient_role?: string;
    }) =>
      fetchAPI<{ type: any }>('/api/document-requirement-types', z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateType: (
      id: string,
      data: {
        display_name?: string;
        description?: string;
        template_document_id?: string | null;
        default_recipient_role?: string;
        active?: boolean;
      },
    ) =>
      fetchAPI<{ type: any }>(`/api/document-requirement-types/${id}`, z.any(), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    deleteType: (id: string) =>
      fetchAPI<{ success: boolean }>(`/api/document-requirement-types/${id}`, z.any(), {
        method: 'DELETE',
      }),

    // ---- Formula: rules ----
    listRules: () =>
      fetchAPI<{ rules: any[] }>('/api/document-requirement-rules', z.any()),

    createRule: (data: {
      requirement_type_id: string;
      project_type_id: string;
      trigger_workflow_stage: string;
      due_offset_days?: number;
      recipient_role: string;
      condition_json?: Record<string, string[]> | null;
      priority?: number;
    }) =>
      fetchAPI<{ rule: any }>('/api/document-requirement-rules', z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    updateRule: (
      id: string,
      data: {
        trigger_workflow_stage?: string;
        due_offset_days?: number;
        recipient_role?: string;
        condition_json?: Record<string, string[]> | null;
        priority?: number;
        active?: boolean;
      },
    ) =>
      fetchAPI<{ rule: any }>(`/api/document-requirement-rules/${id}`, z.any(), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    deleteRule: (id: string) =>
      fetchAPI<{ success: boolean }>(`/api/document-requirement-rules/${id}`, z.any(), {
        method: 'DELETE',
      }),

    importRules: async (csvText: string, createMissingTypes: boolean) => {
      const token = await getAuthToken();
      const formData = new FormData();
      formData.append('file', new Blob([csvText], { type: 'text/csv' }), 'rules.csv');
      const response = await fetch(`${API_BASE_URL}/api/document-requirement-rules/import?create_missing_types=${String(createMissingTypes)}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      return handleResponse(response);
    },

    // ---- Instances: per-project requirements ----
    listForProject: (projectId: string) =>
      fetchAPI<{ requirements: any[] }>(
        `/api/projects/${projectId}/document-requirements`,
        z.any(),
      ),

    addManual: (
      projectId: string,
      data: {
        requirement_type_id: string;
        recipient_role: string;
        custom_recipient_email?: string;
        due_date?: string;
      },
    ) =>
      fetchAPI<{ requirement: any }>(
        `/api/projects/${projectId}/document-requirements`,
        z.any(),
        { method: 'POST', body: JSON.stringify(data) },
      ),

    generate: (projectId: string) =>
      fetchAPI<{ created: number; instances: any[] }>(
        `/api/projects/${projectId}/document-requirements/generate`,
        z.any(),
        { method: 'POST', body: '{}' },
      ),

    update: (
      id: string,
      data: {
        status?: string;
        due_date?: string | null;
        recipient_role?: string;
        custom_recipient_email?: string | null;
        waived_reason?: string;
        notes?: string;
      },
    ) =>
      fetchAPI<{ requirement: any }>(`/api/document-requirements/${id}`, z.any(), {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    send: (id: string) =>
      fetchAPI<{ requirement: any }>(`/api/document-requirements/${id}/send`, z.any(), {
        method: 'POST',
        body: '{}',
      }),

    fulfill: (id: string, data: { document_id: string }) =>
      fetchAPI<{ requirement: any }>(`/api/document-requirements/${id}/fulfill`, z.any(), {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    deleteRequirement: async (id: string) => {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/document-requirements/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      return handleResponse(response);
    },
  },

  // Onboarding — Phase 12
  onboarding: {
    getStatus: () =>
      fetchAPI<OnboardingStatusResponse>(`/api/onboarding/status`, z.any()),

    setup: (data: OnboardingSetupRequest) =>
      fetchAPI<{ success: boolean; firm_type: string; onboarding_status: string; module_visibility: Record<string, boolean> | null }>(
        `/api/onboarding/setup`, z.any(), {
          method: 'PUT',
          body: JSON.stringify(data),
        }),

    complete: () =>
      fetchAPI<{ success: boolean; onboarding_status: string }>(
        `/api/onboarding/complete`, z.any(), {
          method: 'POST',
          body: '{}',
        }),

    skip: () =>
      fetchAPI<{ success: boolean; onboarding_status: string }>(
        `/api/onboarding/skip`, z.any(), {
          method: 'POST',
          body: '{}',
        }),

    updateModules: (module_visibility: Record<string, boolean>) =>
      fetchAPI<{ success: boolean; module_visibility: Record<string, boolean> }>(
        `/api/me/settings/modules`, z.any(), {
          method: 'PUT',
          body: JSON.stringify({ module_visibility }),
        }),

  },

  // Stakeholder Portal — Report Feedback (SPEC-stakeholder-portal-2026-06-23 R3).
  // create() posts one untriaged feedback row; the response is a durable receipt
  // in the SAME allow-listed shape as list() (the "Your Issues" privacy read),
  // so the receipt drops straight into the list. tenant_id + reporter come from
  // the auth context on the backend — never sent from here.
  feedback: {
    // R4/R5: the report is multipart — the reporter's words + up to 5 OPTIONAL
    // images (R5 HIGH-3). We build a FormData and post it with a raw fetch (the
    // shared upload pattern used across this file), because the browser must set
    // the multipart boundary Content-Type itself (fetchWithAuth would force
    // application/json). The receipt is still validated through the same Zod
    // schema as list(). Each image is appended under the SAME `images` field
    // name (FormData supports repeated keys) — this is exactly the field
    // routers/feedback.py's `form.getlist("images")` reads.
    create: async (data: CreateFeedbackRequest) => {
      const form = new FormData();
      form.append('body', data.body);
      if (data.source_screen != null) form.append('source_screen', data.source_screen);
      if (data.route != null) form.append('route', data.route);
      // Finding 5: the idempotency key -> the DB double-submit guard (V193).
      if (data.submit_key) form.append('submit_key', data.submit_key);
      for (const image of data.images ?? []) form.append('images', image);

      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/feedback`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.detail || err?.error || 'Could not submit feedback');
      }
      return createFeedbackResponseSchema.parse(await response.json());
    },

    list: () =>
      fetchAPI(`/api/feedback`, feedbackListResponseSchema),
  },

  // Stakeholder Portal — the gated view + accept loop (SPEC-stakeholder-portal R5).
  // issues() + roadmap() are stakeholder_portal-gated reads (backend-enforced 403);
  // accept()/stillBroken() are resolve_issues-gated write-backs (R2b's real close/
  // reopen recompute). All read through R1's column-allow-list privacy reads.
  stakeholderPortal: {
    issues: () =>
      fetchAPI(`/api/stakeholder-portal/issues`, feedbackListResponseSchema),

    roadmap: () =>
      fetchAPI(`/api/stakeholder-portal/roadmap`, roadmapResponseSchema),

    // R4 AC1: the client HYPE roadmap — top-level epics only (R1's
    // /epic-roadmap). Gated by the SAME stakeholder_portal capability +
    // portal_visibility floor as roadmap() above; RoadmapTab uses this
    // instead of roadmap() so the panel shows the distinct headline+
    // description hype face, not the coordinator-styled ticket board.
    epicRoadmap: () =>
      fetchAPI(`/api/stakeholder-portal/epic-roadmap`, epicRoadmapResponseSchema),

    accept: (feedbackId: string) =>
      fetchAPI(
        `/api/stakeholder-portal/feedback/${feedbackId}/accept`,
        portalAcceptResponseSchema,
        { method: 'POST' }
      ),

    // R1 AC3: durable undo — revert the caller's OWN confirmation (accepted→pending)
    // and reopen; a tri-state safe undo (revert / idempotent no-op / coordinator-
    // terminal refusal). resolve_issues-gated + tenant-scoped on the backend.
    unAccept: (feedbackId: string) =>
      fetchAPI(
        `/api/stakeholder-portal/feedback/${feedbackId}/un-accept`,
        portalUnAcceptResponseSchema,
        { method: 'POST' }
      ),

    // Still-broken is multipart now — the regression note + an OPTIONAL screenshot of
    // what is still wrong. Built as FormData and posted with a raw fetch (the shared
    // upload pattern, same as feedback.create) so the browser sets the multipart
    // boundary Content-Type itself. The receipt is still validated through Zod.
    stillBroken: async (feedbackId: string, note: string, image?: File | null) => {
      const form = new FormData();
      form.append('note', note);
      if (image) form.append('image', image);

      const token = await getAuthToken();
      const response = await fetch(
        `${API_BASE_URL}/api/stakeholder-portal/feedback/${feedbackId}/still-broken`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.detail || err?.error || 'Could not report still-broken');
      }
      return portalAcceptResponseSchema.parse(await response.json());
    },

    // R6 AC1: withdraw ("No longer needed") — a CLIENT-AXIS-ONLY action gated by the
    // lighter stakeholder_portal capability (not resolve_issues). Idempotent + tenant-
    // scoped on the backend; works on an untriaged report too.
    withdraw: (feedbackId: string) =>
      fetchAPI(
        `/api/stakeholder-portal/feedback/${feedbackId}/withdraw`,
        portalWithdrawResponseSchema,
        { method: 'POST' }
      ),

    // R6 AC2: reversible un-withdraw ("changed my mind") — flips withdrawn→pending.
    unWithdraw: (feedbackId: string) =>
      fetchAPI(
        `/api/stakeholder-portal/feedback/${feedbackId}/un-withdraw`,
        portalUnWithdrawResponseSchema,
        { method: 'POST' }
      ),

    // R6 AC4/AC5: add a note — appends to the report thread with NO state change;
    // returns the reporter's own notes echoed back for the row.
    addNote: (feedbackId: string, note: string) =>
      fetchAPI(
        `/api/stakeholder-portal/feedback/${feedbackId}/note`,
        portalAddNoteResponseSchema,
        { method: 'POST', body: JSON.stringify({ note }) }
      ),

    // R4 (SPEC-feedback-return-path-2026-08-17) AC5/AC6/AC8: answer an
    // operator's question, from the same surface the reporter's reports
    // already appear on. Tenant + reporter-scoped on the backend.
    answerQuestion: (feedbackId: string, answer: string) =>
      fetchAPI(
        `/api/stakeholder-portal/feedback/${feedbackId}/answer`,
        portalAnswerQuestionResponseSchema,
        { method: 'POST', body: JSON.stringify({ answer }) }
      ),

    // R2 (header entry + unseen badge): the "your submissions" header icon's
    // badge count + the mark-seen write it triggers on open. Both server-
    // authoritative — no timestamp is ever sent from here.
    unseenCount: () =>
      fetchAPI(
        `/api/stakeholder-portal/unseen-count`,
        portalUnseenCountResponseSchema,
      ),

    markSeen: () =>
      fetchAPI(
        `/api/stakeholder-portal/mark-seen`,
        portalMarkSeenResponseSchema,
        { method: 'POST' }
      ),

    // R3 (tabbed panel shell): the capability-derived tab set. `signal` lets
    // the caller bound the request to a timeout SHORTER than the panel's own
    // load budget (see usePortalPanelConfig) — the fetch is aborted rather
    // than left hanging.
    panelConfig: (signal?: AbortSignal) =>
      fetchAPI(
        `/api/stakeholder-portal/panel-config`,
        portalPanelConfigResponseSchema,
        signal ? { signal } : undefined,
      ),
  },

  // SPEC-stakeholder-portal R6 — the super-admin, cross-tenant internal
  // coordination view + triage actions. The backend gates every endpoint with a
  // DIRECT is_super_admin check at the function boundary.
  coordination: {
    board: () =>
      fetchAPI(`/api/coordination/board`, coordinationBoardResponseSchema),

    // The coordinator DETAIL read behind a work_item's feedback COUNT: each linked
    // reporter's regression note + signed screenshot URLs. Super-admin gated backend.
    workItemFeedback: (workItemId: string) =>
      fetchAPI(
        `/api/coordination/work-items/${workItemId}/feedback`,
        coordinationWorkItemFeedbackResponseSchema
      ),

    promote: (feedbackId: string, body?: { title?: string; description?: string }) =>
      fetchAPI(
        `/api/coordination/feedback/${feedbackId}/promote`,
        coordinationPromoteResponseSchema,
        { method: 'POST', body: JSON.stringify(body ?? {}) }
      ),

    link: (feedbackId: string, workItemId: string) =>
      fetchAPI(
        `/api/coordination/feedback/${feedbackId}/link`,
        coordinationLinkResponseSchema,
        { method: 'POST', body: JSON.stringify({ work_item_id: workItemId }) }
      ),

    // R4 (SPEC-feedback-return-path-2026-08-17) AC1/AC2: ask a question on a
    // report without promoting it or declaring an outcome. Works on an
    // untriaged feedback row.
    askQuestion: (feedbackId: string, question: string) =>
      fetchAPI(
        `/api/coordination/feedback/${feedbackId}/question`,
        coordinationAskQuestionResponseSchema,
        { method: 'POST', body: JSON.stringify({ question }) }
      ),

    // v0.17.16: dismiss an untriaged report without promoting it. Sends no
    // email and creates no work item or GitHub issue. Super-admin only.
    dismiss: (feedbackId: string) =>
      fetchAPI(
        `/api/coordination/feedback/${feedbackId}/dismiss`,
        coordinationDismissResponseSchema,
        { method: 'POST' }
      ),

    setResolution: (
      workItemId: string,
      body: { human_resolution: string; resolution_note?: string }
    ) =>
      fetchAPI(
        `/api/coordination/work-items/${workItemId}/resolution`,
        coordinationResolutionResponseSchema,
        { method: 'PUT', body: JSON.stringify(body) }
      ),

    setPlanning: (
      workItemId: string,
      body: {
        priority?: string;
        planned_release?: string;
        horizon?: string;
        visibility?: string;
      }
    ) =>
      fetchAPI(
        `/api/coordination/work-items/${workItemId}/planning`,
        coordinationPlanningResponseSchema,
        { method: 'PUT', body: JSON.stringify(body) }
      ),

    // CH-03 (v0.17 platform review): re-bridge an orphaned work_item's GitHub
    // link (routers/coordination_view.py::repair_link_endpoint) -- the
    // authorized recovery action for a promote whose GitHub push failed.
    // Safe to call even when the work_item already has an issue (no-op,
    // already_bridged: true).
    repairLink: (workItemId: string) =>
      fetchAPI(
        `/api/coordination/work-items/${workItemId}/repair-link`,
        coordinationRepairLinkResponseSchema,
        { method: 'POST' }
      ),

    // v0.17.15: mark an item shipped by hand, with evidence. Super-admin only.
    // The item reads "Shipped, awaiting your confirmation" to the reporter.
    // No email unless notify_reporters is true.
    markShipped: (
      workItemId: string,
      body: {
        pr_number?: number;
        commit_sha?: string;
        release_tag?: string;
        data_repair_note?: string;
        notify_reporters: boolean;
      }
    ) =>
      fetchAPI(
        `/api/coordination/work-items/${workItemId}/mark-shipped`,
        coordinationMarkShippedResponseSchema,
        { method: 'POST', body: JSON.stringify(body) }
      ),

    // R3 (tabbed panel shell) All-Issues tab: the full outstanding stream
    // (every non-epic work_item + the untriaged queue). Same wire shape as
    // board() (reuses the same cross-tenant reads server-side), so it shares
    // coordinationBoardResponseSchema rather than a duplicate schema. R4 AC3:
    // each work_item row now additionally carries a router-derived
    // claim_ladder (coordinationWorkItemSchema declares it optional).
    outstandingIssues: () =>
      fetchAPI(`/api/coordination/outstanding-issues`, coordinationBoardResponseSchema),

    // R4 AC4: the super-admin epic drill-down (epic + its direct sub-issues,
    // each carrying a derived claim_ladder). Super-admin gated on the
    // backend; a non-super-admin's call 403s regardless of any accidental
    // client-side render attempt.
    epicDrillDown: (epicId: string) =>
      fetchAPI(`/api/coordination/epics/${epicId}`, epicDrillDownResponseSchema),
  },
};

// ============================================================================
// Backward Compatibility - Legacy Functions (DEPRECATED)
// ============================================================================

/**
 * @deprecated Use api.projects.list() instead
 */
export async function fetchProjects(): Promise<Project[]> {
  const result = await api.projects.list();
  return result.projects;
}

/**
 * @deprecated Use api.projects.create() instead
 */
export async function createProject(project: ProjectCreate): Promise<Project> {
  const result = await api.projects.create(project);
  return result.project;
}

/**
 * @deprecated Use api.projects.update() instead
 */
export async function updateProject(id: string, updates: ProjectUpdate): Promise<Project> {
  const result = await api.projects.update(id, updates);
  return result.project;
}

/**
 * @deprecated Use api.projects.delete() instead
 */
export async function deleteProject(id: string): Promise<void> {
  await api.projects.delete(id);
}

/**
 * @deprecated Use api.dashboard.getPreferences() instead
 */
export async function getDashboardPreferences(): Promise<DashboardPreferences> {
  return api.dashboard.getPreferences();
}

/**
 * @deprecated Use api.dashboard.updatePreferences() instead
 */
export async function updateDashboardPreferences(preferences: Partial<DashboardPreferences>): Promise<void> {
  await api.dashboard.updatePreferences(preferences);
}

/**
 * @deprecated Use api.dashboard.toggleWidget() instead
 */
export async function toggleDashboardWidget(widgetName: string, enabled: boolean): Promise<void> {
  await api.dashboard.toggleWidget(widgetName, enabled);
}

/** Read the server's download name; never treat a header as a filesystem path. */
export function invoiceDownloadFilename(disposition: string | null): string | null {
  if (!disposition) return null;
  const extended = /(?:^|;)\s*filename\*\s*=\s*UTF-8'[^']*'([^;]*)/i.exec(disposition);
  let name: string | undefined;
  if (extended) {
    try { name = decodeURIComponent(extended[1].trim()); } catch { /* Use ASCII fallback. */ }
  }
  if (!name) {
    const plain = /(?:^|;)\s*filename\s*=\s*(?:"((?:\\.|[^"\\])*)"|([^;]*))/i.exec(disposition);
    name = plain?.[1]?.replace(/\\(.)/g, '$1') ?? plain?.[2]?.trim();
  }
  const safe = name?.replace(/[<>:"/\\|?*\x00-\x1f\x7f]/g, '').trim().replace(/^[. ]+|[. ]+$/g, '');
  return safe && /\.pdf$/i.test(safe) ? safe : null;
}
